// ManagerDashboard.jsx
import React, { useEffect, useMemo, useState, useContext, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Doughnut, Bar, Line } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, BarElement, LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend } from "chart.js";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import { autoTable } from "jspdf-autotable";
import {
  FaUserGroup, FaUserTag, FaCircleCheck, FaBriefcase, FaTriangleExclamation,
  FaCircleExclamation, FaClock, FaSpinner, FaCheckDouble, FaEye, FaListCheck,
  FaChartPie, FaChartLine, FaStopwatch, FaCalendarDays, FaFlag, FaLayerGroup, FaDatabase,
  FaFileExport, FaFileExcel, FaFilePdf, FaXmark, FaCircleXmark, FaFolderOpen,
  FaComments, FaArrowUpRightFromSquare, FaAward, FaDownload,
  FaUserCheck, FaUserClock, FaChartSimple, FaRankingStar,
} from "react-icons/fa6";
import api, { getMyApprovalQueue } from "../../api";
import { AuthContext } from "../../context/AuthContext";
import { usePermissions } from "../../context/PermissionContext";
import { taskDisplayTitle } from "../../utils/taskDisplay";
import "./ManagerDashboard.css";
import ManagerTaskTable, {
  STATUS_LABELS, getText, normalizeStatus, titleCase, getIdValue,
  getTaskProgress, getProgressText, formatShortDate, statusCls, Spin,
  isDoneStatus, isDeadStatus, isOverdueTask, getMemberRole, getAssigneeId, getTaskCategoryId,
  getDueBadge, getPriorityInfo, buildStatusRows, buildInitialDrafts, buildTeamFilterMembers, filterTaskRows,
} from "./ManagerTaskTable";
import { WORK_GROUP_TABS, filterByWorkGroup, workGroupOf } from "../../utils/workGroup";
import { assignmentErrorMessage } from "../../utils/complaintAssignee";

// Ticket Assignment & Progress: Assign column label per work-flow tab (each
// row's own work group still decides its options).
const FLOW_ASSIGN_LABEL = {
  all: "Assign Owner",
  developer: "Assign Developer",
  "data-analyst": "Assign Data Analyst",
  technician: "Assign Technician",
};

// Same xlsx export pattern already used by DataAnalystDashboard.jsx
// (excelDownload) -- reused as-is by the Manager Dashboard's Generate
// Report modal. Purely a client-side file download of data already loaded
// on this page; no new endpoint.
function excelDownload(sheets, filename) {
  const wb = XLSX.utils.book_new();
  sheets.forEach(({ name, rows }) => {
    const sheet = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, sheet, name.slice(0, 31));
  });
  XLSX.writeFile(wb, filename);
}

// Same chart.js already installed/registered by other dashboards in this
// app (e.g. DataAnalystDashboard.jsx, TechnicianDashboard.js) -- no new
// chart library added. ChartJS.register is idempotent across pages.
ChartJS.register(ArcElement, BarElement, LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend);

const todayDateString = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// User Performance chart plugin (NEW) -- draws the exact completed-task
// count at the end of each horizontal bar, using only Chart.js's own
// canvas plugin hooks (no new library). Always on (screen + PDF export)
// since it's the same number already shown in the ranking table below it.
const barValueLabelPlugin = {
  id: "barValueLabels",
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    chart.data.datasets.forEach((dataset, dsIndex) => {
      const meta = chart.getDatasetMeta(dsIndex);
      meta.data.forEach((bar, i) => {
        const value = dataset.data[i];
        if (value===null || value===undefined) return;
        ctx.save();
        ctx.fillStyle = "#1C1917";
        ctx.font = "700 10px Inter, sans-serif";
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        ctx.fillText(String(value), bar.x + 6, bar.y);
        ctx.restore();
      });
    });
  },
};

// Report chart-snapshot capture (NEW) -- Generate Report's PDF embeds a
// picture of each chart, but every chart on this page keeps its legend OFF
// on screen (a nicer custom HTML legend/summary row is rendered next to it
// instead, which a canvas snapshot can never see). An earlier version of
// this tried to flip the live chart's own Chart.js legend on/off at
// capture time by mutating chart.options.plugins.legend -- that corrupts
// Chart.js's internal scriptable-option resolver cache (crashes with
// "Cannot read properties of undefined (reading 'options')" inside
// Legend.update/generateLabels) and was reverted. Instead: never touch the
// live chart's options at all. Just read its already-rendered canvas
// as-is (plain, safe, always up to date) and, if a legend is wanted, draw
// simple color-swatch + text rows onto a small offscreen canvas below it
// using nothing but the standard 2D canvas API -- no Chart.js internals
// involved, so it can't crash the chart.
function composeChartSnapshot(chart, legendItems) {
  try {
    const canvas = chart?.canvas;
    // A chart's canvas legitimately has width/height 0 when its section is
    // currently hidden (e.g. sitting in a dashboard tab that isn't the
    // active one right now) or hasn't completed its first Chart.js resize
    // pass yet. ctx.drawImage() throws "the image argument is a canvas
    // element with a width or height of 0" for a source canvas like that --
    // treat it as "no chart data available" instead, same as a chart with
    // no ref at all.
    if (!canvas || !chart.toBase64Image || !(canvas.width > 0) || !(canvas.height > 0)) {
      return null;
    }
    if (!legendItems || !legendItems.length) {
      return { img: chart.toBase64Image(), w: canvas.width, h: canvas.height };
    }
    const rowH = 30, padTop = 14, padSide = 16;
    const out = document.createElement("canvas");
    out.width = canvas.width;
    out.height = canvas.height + padTop + legendItems.length*rowH + 6;
    const ctx = out.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, out.width, out.height);
    ctx.drawImage(canvas, 0, 0, canvas.width, canvas.height);
    let y = canvas.height + padTop;
    legendItems.forEach(item=>{
      ctx.fillStyle = item.color;
      ctx.fillRect(padSide, y, 22, 22);
      ctx.fillStyle = "#333333";
      ctx.font = "600 22px Inter, Arial, sans-serif";
      ctx.textBaseline = "middle";
      ctx.fillText(item.text, padSide+32, y+11);
      y += rowH;
    });
    return { img: out.toDataURL("image/png"), w: out.width, h: out.height };
  } catch (err) {
    // Belt-and-braces: a chart-snapshot failure must never crash the whole
    // PDF export -- the caller treats a null return as "no chart data
    // available" and keeps going.
    console.warn("Chart snapshot skipped:", err);
    return null;
  }
}

// Chart.js resizes each canvas asynchronously (ResizeObserver / rAF) right
// after it mounts or its container becomes visible again (e.g. switching
// back to the tab it lives in) -- give any pending resize pass a moment to
// land before composeChartSnapshot() reads canvas.width/height, instead of
// racing it and capturing a still-zero-sized canvas.
function waitForChartsReady() {
  return new Promise(resolve => {
    requestAnimationFrame(() => requestAnimationFrame(() => setTimeout(resolve, 120)));
  });
}

/* ══════════════════════════════════════════
   PURE HELPERS (dashboard-only; the table helpers live in ManagerTaskTable.jsx)
══════════════════════════════════════════ */
const getActivityItems = task => {
  const remarks  = Array.isArray(task?.remarks)        ? task.remarks        : [];
  const history  = Array.isArray(task?.status_history) ? task.status_history : [];
  const rItems = remarks.map(r=>({kind:"comment",id:`r-${r.id}`,time:r.created_at,
    author:getText(r.created_by?.name,"")||getText(r.created_by_name,"")||getText(r.created_by,"Unknown"),
    text:getText(r.remark,""),type:getText(r.remark_type,"general")}));
  const hItems = history.map((h,i)=>({kind:"status",id:`h-${i}`,time:h.changed_at,
    author:getText(h.changed_by?.name,"")||getText(h.changed_by_name,"")||getText(h.changed_by,"Unknown"),
    from:h.from_status,to:h.to_status,note:h.note}));
  return [...rItems,...hItems].sort((a,b)=>new Date(a.time||0)-new Date(b.time||0));
};

const getDueStatus = task => {
  if(!task?.due_at)return{label:"No due date",cls:"jira-muted"};
  const due=new Date(task.due_at),now=new Date();
  if(Number.isNaN(due.getTime()))return{label:"Invalid",cls:"jira-muted"};
  if(["completed","closed","resolved"].includes(normalizeStatus(task.status)))return{label:"Completed",cls:"jira-success"};
  if(due<now)return{label:"Overdue",cls:"jira-danger"};
  return{label:"Within TAT",cls:"jira-success"};
};

const getTatLabel = task => {
  const v=task?.sub_category?.tat_value||task?.sub_category_tat_value||task?.tat_value;
  const u=task?.sub_category?.tat_unit ||task?.sub_category_tat_unit ||task?.tat_unit;
  return v&&u?`${v} ${u}`:"Not set";
};

/* ══════════════════════════════════════════
   NEW PURE HELPERS — dashboard-only, additive.
   These only ever read/derive from data the page already fetches; they
   never call an API and never change what a task/member "is".
══════════════════════════════════════════ */
function greetingWord(){
  const h=new Date().getHours();
  if(h<12)return "Good Morning";
  if(h<17)return "Good Afternoon";
  return "Good Evening";
}
const startOfDay = v => { const d=new Date(v); d.setHours(0,0,0,0); return d; };
const endOfDay   = v => { const d=new Date(v); d.setHours(23,59,59,999); return d; };

function periodRange(period, customFrom, customTo, now){
  if(period==="today") return [startOfDay(now), endOfDay(now)];
  if(period==="week"){
    const day=now.getDay(); const diffToMonday = day===0?-6:1-day;
    const monday=new Date(now); monday.setDate(now.getDate()+diffToMonday);
    return [startOfDay(monday), endOfDay(now)];
  }
  if(period==="month"){ const first=new Date(now.getFullYear(),now.getMonth(),1); return [startOfDay(first), endOfDay(now)]; }
  if(period==="custom") return [customFrom?startOfDay(new Date(customFrom)):null, customTo?endOfDay(new Date(customTo)):null];
  return [null,null]; // "all"
}

function timeAgo(v){
  if(!v)return "";
  const d=new Date(v); if(Number.isNaN(d.getTime()))return "";
  const diffMs = Date.now()-d.getTime();
  if(diffMs<0) return formatShortDate(v);
  const min = Math.floor(diffMs/60000);
  if(min<1) return "Just now";
  if(min<60) return `${min} minute${min===1?"":"s"} ago`;
  const hr = Math.floor(min/60);
  if(hr<24) return `${hr} hour${hr===1?"":"s"} ago`;
  const day = Math.floor(hr/24);
  if(day<30) return `${day} day${day===1?"":"s"} ago`;
  return formatShortDate(v);
}

// Workload must reflect CURRENT active work, never the historical total --
// a member with 130 completed and 0 open tasks is idle today, not
// "High load". `active` here is always pending+in_progress+assigned+open+
// any other non-terminal status already defined above (isDeadStatus), so
// it stays correct for any status the project adds without new rules.
const WORKLOAD_LEVELS = { none:"Available", low:"Low", balanced:"Balanced", high:"High" };
const workloadLevelFor = active => active===0 ? "none" : active<=2 ? "low" : active<=5 ? "balanced" : "high";
const workloadCls = level => `md-wtag-${level||"none"}`;

// Capacity level for the consolidated Team Workload / Capacity panel (§5) --
// a dedicated LOW/NORMAL/HIGH/OVERLOADED 4-tier read of the SAME `active`
// count workloadLevelFor already buckets above; kept as its own function
// (rather than changing workloadLevelFor itself) so every other existing
// consumer of workloadLevelFor/WORKLOAD_LEVELS/workloadCls elsewhere on this
// page (My Team badges, ownerStatusInfo, User Performance Report's Workload
// column) keeps its current wording and thresholds unchanged.
const capacityLevelFor = active => active<=1 ? "low" : active<=4 ? "normal" : active<=7 ? "high" : "overloaded";
const CAPACITY_LABELS = { low:"Low", normal:"Normal", high:"High", overloaded:"Overloaded" };

// Priority-aware status badge for a team member -- overdue work always wins
// (it's the "immediate exception" a manager needs to see first), then
// idle/available, then the existing workload level. Purely a display
// re-ranking of fields already computed by buildOwnerStats (active/overdue/
// workloadLevel) -- no new data, no new business rule.
const ownerStatusInfo = m => {
  if(m.overdue>0) return { label:"Overdue", level:"overdue" };
  if(m.active===0) return { label:"Available", level:"none" };
  return { label:WORKLOAD_LEVELS[m.workloadLevel], level:m.workloadLevel };
};

// Performance Status (NEW, UI-only) -- a quality-of-output label distinct
// from the workload-level badge above (that one is about CURRENT load;
// this one is about completion track record), built purely from fields
// buildOwnerStats already computes (completionRate/overdue). Thresholds are
// a display classification only -- they never feed back into any
// assignment/SLA/workflow calculation.
const performanceStatusFor = m => {
  if(m.assigned===0) return null;
  const rate = m.completionRate||0;
  if(m.overdue>0 && rate<50) return {label:"At Risk", cls:"risk"};
  if(rate>=90) return {label:"Excellent", cls:"excellent"};
  if(rate>=70) return {label:"Good", cls:"good"};
  return {label:"Needs Improvement", cls:"fair"};
};

// My Team card icon/tone lookup (UI-only) -- collapses ownerStatusInfo's 5
// levels (none/low/balanced/high/overdue) down to the 4 visual buckets the
// card design uses (Available/Active/High Workload/Overdue), purely for
// picking an icon + a subtle card-background tone. Does not change
// ownerStatusInfo/workloadCls themselves or any value they still drive
// elsewhere (Workload Overview table, workload badges, etc.).
const MYTEAM_STATUS_META = {
  none:     { icon:FaUserCheck,            tone:"available" },
  low:      { icon:FaUserClock,            tone:"active" },
  balanced: { icon:FaBriefcase,            tone:"active" },
  high:     { icon:FaTriangleExclamation,  tone:"warning" },
  overdue:  { icon:FaCircleExclamation,    tone:"danger" },
};

// Same tone→color mapping as the .md-badge-priority CSS classes above
// (low/medium/high/critical), reused for the Priority Distribution chart so
// a given priority reads identically everywhere on the page.
const PRIORITY_COLORS = { critical:"#DC2626", high:"#D97706", medium:"#2563EB", low:"#16A34A" };

/* ════════════════════════════════════════════════════════
   COMPONENT
════════════════════════════════════════════════════════ */
export default function ManagerDashboard() {
  const { user } = useContext(AuthContext)||{};
  const { can } = usePermissions()||{};
  const navigate = useNavigate();
  const token = localStorage.getItem("token")||"";
  const cfg   = token?{headers:{Authorization:`Bearer ${token}`}}:{};

  /* ── data ── */
  const [tasks,       setTasks]       = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]); // NEW: full manager-associated roster (all roles), additive alongside `technicians`
  const [units,       setUnits]       = useState([]);
  const [categories,  setCategories]  = useState([]);
  const [createCats,  setCreateCats]  = useState([]);
  const [createSubs,  setCreateSubs]  = useState([]);
  const [drafts,      setDrafts]      = useState({});

  /* ── filters (existing Ticket Assignment & Progress table) ── */
  const [search,     setSearch]     = useState("");
  const [statusF,    setStatusF]    = useState("ALL");
  const [page,       setPage]       = useState(1);
  const [rowsPerPage,setRowsPerPage]= useState(10);
  // NEW: quick-filter chips above the task table (§14) — a purely additive,
  // client-side AND-condition on top of the existing search/statusF/gMember;
  // defaults to "all" (a no-op) so existing filtering behavior is unchanged
  // unless a chip is actually clicked.
  const [quickFilter, setQuickFilter] = useState("all");
  // Work-flow tab (All / Developer / Data Analyst / Technician / IT Support)
  // -- the same tabs as Manager -> Team Complaints (utils/workGroup.js).
  const [flowTab, setFlowTab] = useState("all");

  /* ── NEW: global dashboard filter bar (drives KPIs/attention/team/
     analytics/trend/recent-activity everywhere; the "current owner" filter
     on the Ticket Assignment & Progress table reuses gMember directly so the
     two stay in sync — the table keeps its own search/statusF on top) ── */
  const [gPeriod,     setGPeriod]     = useState("all");
  const [gCustomFrom, setGCustomFrom] = useState("");
  const [gCustomTo,   setGCustomTo]   = useState("");
  const [gMember,     setGMember]     = useState("ALL");
  const [gRole,       setGRole]       = useState("ALL");
  const [gStatus,     setGStatus]     = useState("ALL");
  const [gPriority,   setGPriority]   = useState("ALL");
  const [gCategory,   setGCategory]   = useState("ALL");
  const [teamSearch,  setTeamSearch]  = useState("");
  const [teamShowAll, setTeamShowAll] = useState(false);
  const [trendGranularity, setTrendGranularity] = useState("weekly");
  const [userTasksShowAll, setUserTasksShowAll] = useState(false); // NEW: user-wise detail panel's own task list
  const [categoryShowAll, setCategoryShowAll] = useState(false); // NEW: Issue Trend Analysis card's "View All Categories" toggle
  const [workloadSort, setWorkloadSort] = useState("tasks"); // NEW: User Performance Report table's own sort control
  const [workloadPage, setWorkloadPage] = useState(1); // NEW: User Performance Report table's own pagination
  const workloadRowsPerPage = 8;
  const [capacityShowAll, setCapacityShowAll] = useState(false); // NEW: Team Workload/Capacity panel's own "view all" toggle

  // NEW: Generate Report modal (filter-based report preview + PDF/Excel
  // download) -- purely additive UI state, no existing filter/data flow
  // touched; the report always reads the same globalFilteredTasks/
  // dashboardStats/etc. every other section on this page already uses.
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportGenerating, setReportGenerating] = useState(null); // null | "pdf" | "excel"

  // NEW: the report modal's OWN period/member filter -- independent of the
  // page-wide gPeriod/gMember, so adjusting Period/Member inside the modal
  // never touches the dashboard behind it (same "own filter state" pattern
  // as previewPeriod/previewMemberId for the User Performance popup below).
  // Seeded from gPeriod/gMember every time the modal (re)opens via
  // openReportModal, then free to change on its own.
  const [reportPeriod, setReportPeriod] = useState("all");
  const [reportCustomFrom, setReportCustomFrom] = useState("");
  const [reportCustomTo, setReportCustomTo] = useState("");
  const [reportMember, setReportMember] = useState("ALL");
  const openReportModal = () => {
    setReportPeriod(gPeriod); setReportCustomFrom(gCustomFrom); setReportCustomTo(gCustomTo); setReportMember(gMember);
    setShowReportModal(true);
  };

  // NEW: User Performance Preview popup (§6) -- identified by
  // previewMemberId (below), NOT gMember, so opening it (from either the
  // My Team cards or the User Performance Report table, both via
  // previewUserDetails) never touches the page-wide member filter behind it.
  const [showUserPreview, setShowUserPreview] = useState(false);

  // NEW: the popup's own time filter -- independent of the page-wide
  // gPeriod/gCustomFrom/gCustomTo above so changing it never touches the
  // dashboard behind the popup. Seeded from gPeriod each time the popup is
  // (re)opened (see openUserPreview below), then free to change on its own.
  const [previewPeriod, setPreviewPeriod] = useState("all");
  const [previewCustomFrom, setPreviewCustomFrom] = useState("");
  const [previewCustomTo, setPreviewCustomTo] = useState("");

  // NEW: which member the popup itself is showing -- independent of
  // gMember, so opening it from the User Performance Report table's "View"
  // button (previewUserDetails below) never touches the page-wide member
  // filter, "Viewing:" bar, or any gMember-driven section behind the popup.
  const [previewMemberId, setPreviewMemberId] = useState(null);

  // NEW: top-level tab navigation (§ redesign) -- purely a UI grouping of
  // sections that already exist below; no section's own state/handlers are
  // touched, tabs just control which already-rendered block is visible.
  const [activeTab, setActiveTab] = useState("overview");

  /* ── NEW: staff-approval queue summary (reuses the existing, already
     -implemented getMyApprovalQueue API function — same one MyApprovals.js
     already calls; no new endpoint, no backend change) ── */
  const [approvalQueue,   setApprovalQueue]   = useState([]);
  const [approvalLoading, setApprovalLoading] = useState(false);

  // NEW: header "last updated" timestamp (§1) -- purely a display value set
  // alongside the existing fetchDashboardData success path below; nothing
  // reads or branches on it, so it can't affect any existing behavior.
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  /* ── loading ── */
  const [loading,   setLoading]  = useState(false);
  const [savingId,  setSavingId] = useState(null);
  const [creating,  setCreating] = useState(false);

  /* ── create modal ── */
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({title:"",description:"",unit_id:"",category_id:"",sub_category_id:"",assigned_to:"",task_date:todayDateString()});

  /* ── ticket panel ── */
  const [showTicket,      setShowTicket]      = useState(false);
  const [ticketTask,      setTicketTask]      = useState(null);
  const [ticketLoading,   setTicketLoading]   = useState(false);
  const [activityTab,     setActivityTab]     = useState("all");
  const [comment,         setComment]         = useState("");
  const [commentSaving,   setCommentSaving]   = useState(false);
  const feedEndRef = useRef(null);
  const taskTableRef = useRef(null);
  const teamRef = useRef(null);
  const userDetailRef = useRef(null);
  // NEW: chart instance refs (react-chartjs-2 forwards these to the
  // underlying Chart.js object, which exposes .toBase64Image()) -- used
  // only to embed "graph snapshots" into the Generate Report PDF (§7).
  // Purely additive; nothing about how these charts render changes.
  const trendChartRef = useRef(null);
  const userPerfChartRef = useRef(null);
  // NEW: User Performance Preview popup's OWN chart instances (§9) -- kept
  // separate from trendChartRef above on purpose. That ref is already
  // attached to the always-mounted Team Performance Trend section
  // and read by the (unrelated) Generate Report PDF; the popup only mounts
  // conditionally, and a single ref object can only ever point at whichever
  // of its two consumers rendered most recently -- reusing the same refs
  // here would silently steal them the moment the popup opens and corrupt
  // Generate Report's chart snapshots. Dedicated refs keep the two features
  // from ever touching each other's captured canvas.
  const userPreviewStatusChartRef = useRef(null);
  const userPreviewSlaChartRef = useRef(null);
  const userPreviewTrendChartRef = useRef(null);
  // NEW: Performance Analytics tab's own inline Ticket Status donut -- a
  // separate ref from userPreviewStatusChartRef for the same reason as
  // above (two mounted consumers can't safely share one chart ref).
  const perfStatusChartRef = useRef(null);

  /* ── toast ── */
  const [toast, setToast] = useState({show:false,msg:"",variant:"success"});
  const pop = (msg, variant="success") => { setToast({show:true,msg,variant}); setTimeout(()=>setToast(t=>({...t,show:false})),3000); };

  // Each target section now lives inside its own tab panel (Ticket
  // Management / Team Management / Performance Analytics respectively).
  // These helpers switch to that tab first, then scroll on the next tick
  // once the panel is visible -- the section itself, its ref and every
  // handler on it are unchanged.
  const scrollToTaskTable = () => { setActiveTab("tasks"); setTimeout(()=>taskTableRef.current?.scrollIntoView({behavior:"smooth",block:"start"}),60); };
  const scrollToTeam      = () => { setActiveTab("team"); setTimeout(()=>teamRef.current?.scrollIntoView({behavior:"smooth",block:"start"}),60); };
  // KPI count cards open the Complaints page (same tab), pre-filtered to
  // the card's status where the page supports one.
  const openComplaints = status => () => navigate(status ? `/manager/complaints?status=${status}` : "/manager/complaints");
  const scrollToUserDetail = () => { setActiveTab("performance"); setTimeout(()=>userDetailRef.current?.scrollIntoView({behavior:"smooth",block:"start"}),60); };
  // "View Details" on a My Team card and "View" in the User Performance
  // Report table both open this same popup, identified by previewMemberId --
  // NOT the page-wide gMember filter. So opening it never touches the
  // member filter / "Viewing:" bar / Top Performers chart behind it, and
  // closing the popup leaves the dashboard exactly as it was.
  // Seeds the popup's own previewPeriod/previewCustomFrom/previewCustomTo
  // from the current gPeriod/gCustomFrom/gCustomTo every time it opens, so
  // it starts in sync with the dashboard before the manager narrows it
  // further.
  const openUserPreview = memberId => {
    setPreviewMemberId(memberId);
    setPreviewPeriod(gPeriod); setPreviewCustomFrom(gCustomFrom); setPreviewCustomTo(gCustomTo);
    setShowUserPreview(true);
  };

  const previewUserDetails = memberId => openUserPreview(memberId);

  /* ══ fetch ══ */
  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      // Role/sidebar policy follow-up: the technician-filter dropdown below
      // must only ever list this manager's OWN unit's employees, never
      // every user in the system -- /manager/team-users derives the unit
      // exclusively from the caller's trusted session (req.user.unit_id),
      // never a client-supplied value, unlike the generic /users endpoint
      // this used to call (which has no scoping at all and is left
      // untouched for its many other, unrelated consumers).
      const [taskRes,userRes,unitRes,catRes] = await Promise.allSettled([
        api.get(`/tasks`,      cfg),
        api.get(`/manager/team-users`, cfg),
        api.get(`/units`,      cfg),
        api.get(`/categories`, cfg),
      ]);
      const taskData = taskRes.status==="fulfilled"&&Array.isArray(taskRes.value.data) ? taskRes.value.data : [];
      const userData = userRes.status==="fulfilled"&&Array.isArray(userRes.value.data) ? userRes.value.data : [];
      const unitData = unitRes.status==="fulfilled"&&Array.isArray(unitRes.value.data) ? unitRes.value.data : [];
      const catData  = catRes.status==="fulfilled" &&Array.isArray(catRes.value.data)  ? catRes.value.data  : [];

      const cleaned = taskData.map(t=>({...t,status:normalizeStatus(t.status)}));
      const techs   = userData.filter(u=>{
        const r=[u.role,u.role_name,u.roleName,u.roles?.name].map(x=>getText(x,"")).filter(Boolean).join(" ").toLowerCase();
        return r.includes("technician")||Number(u.role_id)===4;
      });

      setTasks(cleaned);
      setTechnicians(techs.length?techs:userData);
      setTeamMembers(userData); // full associated roster (every role), for "My Team" / workload — never filtered down to technicians only
      setUnits(unitData);
      setCategories(catData);

      setDrafts(buildInitialDrafts(cleaned));
      setLastUpdatedAt(new Date());
    } catch(e) { pop(e?.response?.data?.error||"Failed to load dashboard","danger"); }
    finally { setLoading(false); }
  }, []);

  const fetchCatsForCreate = async uid => {
    try {
      const {data}=await api.get(`/categories/by-unit/${uid}`,cfg);
      setCreateCats(Array.isArray(data)?data:[]);
      setCreateSubs([]);
      setCreateForm(p=>({...p,category_id:"",sub_category_id:""}));
    } catch {
      setCreateCats(categories.filter(c=>!c.unit_id||String(c.unit_id)===String(uid)));
    }
  };

  const fetchSubsForCreate = async catId => {
    try {
      if(!createForm.unit_id){setCreateSubs([]);return;}
      const {data}=await api.get(`/subcategories/by-category/${catId}/unit/${createForm.unit_id}`,cfg);
      setCreateSubs(Array.isArray(data)?data:[]);
    } catch { setCreateSubs([]); }
  };

  /* ── load ticket detail ── */
  const loadTicket = async (task, showLoad=true) => {
    if(!task?.id)return;
    try {
      if(showLoad) setTicketLoading(true);
      const {data}=await api.get(`/tasks/${task.id}`,cfg);
      setTicketTask({...task,...data,status:normalizeStatus(data?.status||task.status)});
    } catch { if(showLoad) setTicketTask(task); }
    finally { if(showLoad) setTicketLoading(false); }
  };

  /* ── effects ── */
  useEffect(()=>{ fetchDashboardData(); },[fetchDashboardData]);
  useEffect(()=>{ setWorkloadPage(1); },[workloadSort, teamSearch, gRole, gMember]);
  useEffect(()=>{ if(!createForm.unit_id){setCreateCats([]);setCreateSubs([]);return;} fetchCatsForCreate(createForm.unit_id); },[createForm.unit_id]);
  useEffect(()=>{ if(!createForm.category_id){setCreateSubs([]);return;} fetchSubsForCreate(createForm.category_id); },[createForm.category_id]);
  useEffect(()=>{ feedEndRef.current?.scrollIntoView({behavior:"smooth"}); },[ticketTask?.remarks?.length,ticketTask?.status_history?.length,activityTab]);

  /* auto-refresh ticket */
  useEffect(()=>{
    if(!showTicket||!ticketTask?.id)return;
    const t=setInterval(()=>loadTicket(ticketTask,false),4000);
    return ()=>clearInterval(t);
  },[showTicket,ticketTask?.id]);

  /* NEW: staff-approval queue summary — additive read-only fetch, reusing
     the existing getMyApprovalQueue() function (already used by
     pages/manager/MyApprovals.js). Purely for the "Pending Staff Approvals"
     dashboard card; never touches the approval actions themselves. */
  useEffect(()=>{
    let alive=true;
    if(!user?.id) return;
    setApprovalLoading(true);
    getMyApprovalQueue(user.id)
      .then(({data})=>{ if(alive) setApprovalQueue(Array.isArray(data?.items)?data.items:[]); })
      .catch(()=>{ if(alive) setApprovalQueue([]); })
      .finally(()=>{ if(alive) setApprovalLoading(false); });
    return ()=>{ alive=false; };
  },[user?.id]);

  /* ── create task (unchanged logic) ── */
  // A Manager raises tickets for their own unit only (the backend rejects
  // any other unit_id). GET /units returns just that unit for a Manager, so
  // the server list wins over the cached session's unit_id.
  const ownUnitId = units.length===1 ? String(getIdValue(units[0])||"") : String(user?.unit_id||"");
  const ownUnitName = getText(units.find(u=>String(getIdValue(u))===ownUnitId),"") || "-";
  const resetCreate = ()=>{ setCreateForm({title:"",description:"",unit_id:ownUnitId,category_id:"",sub_category_id:"",assigned_to:"",task_date:todayDateString()}); setCreateCats([]); setCreateSubs([]); };

  // Estimated TAT -- read-only display of the Subcategory's own configured
  // tat_value/tat_unit (services/subcategoryTatService.js is the single
  // source of truth). Never editable here and never sent in the payload --
  // the backend independently resolves TAT from sub_category_id alone.
  const selectedCreateSub = createSubs.find(s=>String(s.id)===String(createForm.sub_category_id));
  const createSubHasTat = !!(selectedCreateSub && selectedCreateSub.tat_value!=null && selectedCreateSub.tat_unit);

  const createTask = async () => {
    if(!createForm.title||!createForm.unit_id||!createForm.category_id||!createForm.sub_category_id||!createForm.task_date){ pop("Please fill all required fields","danger"); return; }
    try {
      setCreating(true);
      const payload={title:createForm.title.trim(),description:createForm.description.trim(),unit_id:Number(createForm.unit_id),category_id:Number(createForm.category_id),sub_category_id:Number(createForm.sub_category_id),created_by:user?.id,task_date:createForm.task_date};
      let id=null;
      if(createForm.assigned_to){
        const r=await api.post(`/admin-assign`,{...payload,assigned_to:Number(createForm.assigned_to)},cfg);
        id=r?.data?.id||r?.data?.task_id||r?.data?.task?.id||r?.data?.insertId||r?.data?.data?.id;
      } else {
        const r=await api.post(`/tasks`,payload,cfg);
        id=r?.data?.id||r?.data?.task_id||r?.data?.task?.id||r?.data?.insertId||r?.data?.data?.id;
      }
      setShowCreate(false); resetCreate(); await fetchDashboardData();
      pop(id?`Ticket #${id} created`:"Ticket created successfully");
    } catch(e){ pop(e?.response?.data?.error||e?.response?.data?.message||"Failed to create ticket","danger"); }
    finally{ setCreating(false); }
  };

  /* ── assign ── */
  const saveAssignment = async task => {
    const draft=drafts[task.id]||{};
    if(!draft.assigned_to){ pop("Please select an assignee","danger"); return; }
    try {
      setSavingId(task.id);
      // (A best-effort PUT /tasks/:id/category used to follow -- that
      // endpoint never existed, so it always failed silently; removed.)
      await api.put(`/tasks/assign/${task.id}`,{assigned_to:Number(draft.assigned_to)},cfg);
      await fetchDashboardData(); pop("Ticket assigned successfully");
    } catch(e){ pop(assignmentErrorMessage(e,"Failed to assign ticket"),"danger"); }
    finally{ setSavingId(null); }
  };

  /* ── open ticket panel ── */
  const openTicket = async task => {
    setTicketTask(task); setActivityTab("all"); setComment(""); setShowTicket(true);
    await loadTicket(task,true);
  };

  /* ── add manager comment ── */
  const addComment = async () => {
    const text=comment.trim();
    if(!ticketTask?.id||!text||commentSaving)return;
    try {
      setCommentSaving(true);
      await api.post(`/tasks/${ticketTask.id}/remarks`,{remark:text,remark_type:"general",created_by:user?.id},cfg);
      setComment(""); await loadTicket(ticketTask,false); await fetchDashboardData();
      pop("Comment added");
    } catch(e){ pop(e?.response?.data?.error||"Failed to add comment","danger"); }
    finally{ setCommentSaving(false); }
  };

  /* ── derived (Ticket Assignment & Progress table) ── */
  // Status dropdown options (shared helper; Closed/Cancelled not offered).
  const statusRows = useMemo(()=>buildStatusRows(tasks),[tasks]);

  // The Ticket Assignment & Progress table shows ALL own-unit team work --
  // Developer, Data Analyst and Technician / IT Support -- in work-flow tabs,
  // like Manager -> Team Complaints. The tab is the row's server-derived
  // work_group (ticket_flow / task type / owner role, services/
  // ticketFlowService.js), never the creator's role alone: a Developer-raised
  // IT Support complaint is Technician work, a Developer Own Ticket is
  // Developer work. A row with no work group (e.g. unassigned ERP, Staff-owned
  // GENERAL) is not team work and is left out. The server already scopes
  // GET /tasks by unit; the unit check only guards the render. The KPI/
  // analytics sections still read the full `tasks` set.
  const teamWorkTasks = useMemo(()=>{
    const unitId=user?.unit_id;
    return tasks
      .filter(t=>workGroupOf(t))
      .filter(t=>!unitId||t.unit_id===null||t.unit_id===undefined||Number(t.unit_id)===Number(unitId));
  },[tasks, user?.unit_id]);
  // Priority / Category (synced with the global bar, like the member filter)
  // narrow the table before the tab, so tab counts honour them too.
  const tableBaseRows = useMemo(()=>teamWorkTasks.filter(t=>{
    if(gPriority!=="ALL" && String(t.priority||"").toLowerCase()!==gPriority) return false;
    if(gCategory!=="ALL" && getTaskCategoryId(t)!==gCategory) return false;
    return true;
  }),[teamWorkTasks, gPriority, gCategory]);
  // Chips, status options and pagination see only the selected tab's rows.
  const flowRows = useMemo(()=>filterByWorkGroup(tableBaseRows, flowTab),[tableBaseRows, flowTab]);
  const flowStatusRows = useMemo(()=>buildStatusRows(flowRows),[flowRows]);
  const changeFlowTab = key => { setFlowTab(key); setPage(1); };
  const resetTaskTable = () => {
    setFlowTab("all"); setSearch(""); setStatusF("ALL"); setGMember("ALL");
    setGPriority("ALL"); setGCategory("ALL"); setQuickFilter("all"); setPage(1);
  };

  const activityItems = useMemo(()=>{
    const all=getActivityItems(ticketTask);
    if(activityTab==="comments")return all.filter(i=>i.kind==="comment");
    if(activityTab==="history") return all.filter(i=>i.kind==="status");
    return all;
  },[ticketTask,activityTab]);

  const updateDraft = (id,f,v)=>setDrafts(p=>({...p,[id]:{...(p[id]||{}),[f]:v}}));

  /* ══════════════════════════════════════════════════════════
     NEW derived data — operational control-center view. Every value
     below is grouped/filtered/aggregated client-side from `tasks`,
     `teamMembers`/`technicians`, `categories` and `approvalQueue`
     (all already fetched above); nothing here calls a new endpoint or
     changes what any existing state/handler does.
  ══════════════════════════════════════════════════════════ */
  const dashNow = useMemo(()=>new Date(), [tasks]);

  // `/manager/team-users` is scoped server-side to the manager's own unit,
  // but a unit can contain more than just this manager's direct reports
  // (other managers, admins, general "Staff", etc. sharing the same unit).
  // "My Team" / "Team Workload" — and anything driven by their Member/Role
  // filters — must only ever show this manager's own operational
  // subordinates, so: drop the logged-in manager's own record (self) and
  // any Manager/Admin/Staff-role record. Never touches `teamMembers` itself
  // (still consumed as-is by any other existing behavior) or the
  // `technicians` list the existing Assign Tech dropdown already relies on.
  const myTeamMembers = useMemo(()=>buildTeamFilterMembers(teamMembers, user?.id),[teamMembers, user?.id]);

  const roleOptions = useMemo(()=>Array.from(new Set(myTeamMembers.map(getMemberRole))).sort(), [myTeamMembers]);

  const priorityOptions = useMemo(()=>Array.from(new Set(tasks.map(t=>String(t.priority||"").toLowerCase()).filter(Boolean))), [tasks]);

  // Work-flow tab counts honour the table's other filters (search / status /
  // user / chip, plus priority / category in tableBaseRows), so each number
  // matches the rows that tab would show.
  const flowCounts = useMemo(()=>{
    const visible=filterTaskRows(tableBaseRows, {search, status:statusF, member:gMember, quickFilter}, dashNow);
    return Object.fromEntries(WORK_GROUP_TABS.map(([key])=>[key, filterByWorkGroup(visible, key).length]));
  },[tableBaseRows, search, statusF, gMember, quickFilter, dashNow]);
  useEffect(()=>{ setPage(1); },[gPriority, gCategory]);

  const globalFilteredTasks = useMemo(()=>{
    const [from,to] = periodRange(gPeriod, gCustomFrom, gCustomTo, dashNow);
    return tasks.filter(t=>{
      if(from||to){
        const c = t.created_at ? new Date(t.created_at) : null;
        if(!c||Number.isNaN(c.getTime())) return false;
        if(from&&c<from) return false;
        if(to&&c>to) return false;
      }
      if(gStatus!=="ALL" && normalizeStatus(t.status)!==gStatus) return false;
      if(gPriority!=="ALL" && String(t.priority||"").toLowerCase()!==gPriority) return false;
      if(gCategory!=="ALL" && getTaskCategoryId(t)!==gCategory) return false;
      const av = getAssigneeId(t);
      if(gMember!=="ALL" && av!==gMember) return false;
      if(gRole!=="ALL"){
        const member = myTeamMembers.find(m=>String(getIdValue(m))===av);
        if(!member || getMemberRole(member)!==gRole) return false;
      }
      return true;
    });
  },[tasks, gPeriod, gCustomFrom, gCustomTo, gStatus, gPriority, gCategory, gMember, gRole, myTeamMembers, dashNow]);

  const hasActiveGlobalFilters = gPeriod!=="all"||gMember!=="ALL"||gRole!=="ALL"||gStatus!=="ALL"||gPriority!=="ALL"||gCategory!=="ALL";
  const resetGlobalFilters = () => {
    setGPeriod("all"); setGCustomFrom(""); setGCustomTo("");
    setGMember("ALL"); setGRole("ALL"); setGStatus("ALL"); setGPriority("ALL"); setGCategory("ALL");
  };

  const PERIOD_LABELS = {all:"All Time", today:"Today", week:"This Week", month:"This Month", custom:"Custom Range"};
  // Every KPI/attention/exceptions/team/analytics/trend section below reads
  // globalFilteredTasks, so this is the single label describing what all of
  // them are currently scoped to — kept next to the filter bar so the
  // manager never has to guess whether a number reflects "All Time" or a
  // narrowed view. The Ticket Assignment & Progress table is intentionally
  // NOT included (it keeps its own independent search/status/technician
  // filters, called out separately near that table).
  const viewingLabel = useMemo(()=>{
    const parts = [PERIOD_LABELS[gPeriod]||"All Time"];
    if(gMember!=="ALL"){
      const m=myTeamMembers.find(x=>String(getIdValue(x))===gMember);
      parts.push(m?getText(m):"Selected Member");
    } else parts.push("All Members");
    parts.push(gRole==="ALL"?"All Roles":gRole);
    if(gStatus!=="ALL") parts.push(STATUS_LABELS[gStatus]||titleCase(gStatus));
    if(gPriority!=="ALL") parts.push(`${titleCase(gPriority)} Priority`);
    if(gCategory!=="ALL"){
      const c=categories.find(x=>String(getIdValue(x))===gCategory);
      parts.push(c?getText(c):"Selected Category");
    }
    return parts.join(" • ");
  },[gPeriod, gMember, gRole, gStatus, gPriority, gCategory, myTeamMembers, categories]);

  // Generate Report modal (NEW) -- same lookups viewingLabel already does,
  // but as discrete chips (only the filters actually narrowed down, per the
  // report spec's example) instead of one joined string.
  const reportFilterChips = useMemo(()=>{
    const chips = [];
    if(gPeriod!=="all") chips.push({key:"period", label:PERIOD_LABELS[gPeriod]||titleCase(gPeriod)});
    if(gMember!=="ALL"){
      const m=myTeamMembers.find(x=>String(getIdValue(x))===gMember);
      chips.push({key:"member", label:m?getText(m):"Selected Member"});
    }
    if(gRole!=="ALL") chips.push({key:"role", label:gRole});
    if(gStatus!=="ALL") chips.push({key:"status", label:STATUS_LABELS[gStatus]||titleCase(gStatus)});
    if(gPriority!=="ALL") chips.push({key:"priority", label:`${titleCase(gPriority)} Priority`});
    if(gCategory!=="ALL"){
      const c=categories.find(x=>String(getIdValue(x))===gCategory);
      chips.push({key:"category", label:c?getText(c):"Selected Category"});
    }
    if(!chips.length) chips.push({key:"all", label:"All Time • All Members • All Roles"});
    return chips;
  },[gPeriod, gMember, gRole, gStatus, gPriority, gCategory, myTeamMembers, categories]);

  /* KPI summary (section 3) */
  const dashboardStats = useMemo(()=>{
    const total = globalFilteredTasks.length;
    const assigned = globalFilteredTasks.filter(t=>getAssigneeId(t)||getText(t.assigned_to_name,"")).length;
    const unassigned = total-assigned;
    const pending = globalFilteredTasks.filter(t=>normalizeStatus(t.status)==="pending").length;
    const inProgress = globalFilteredTasks.filter(t=>["in_progress","open"].includes(normalizeStatus(t.status))).length;
    const completed = globalFilteredTasks.filter(t=>isDoneStatus(normalizeStatus(t.status))).length;
    const active = globalFilteredTasks.filter(t=>!isDeadStatus(normalizeStatus(t.status))).length; // pending + in_progress + assigned + open + any other non-terminal status
    const overdue = globalFilteredTasks.filter(t=>isOverdueTask(t,dashNow)).length;
    const avgProgress = total? Math.round(globalFilteredTasks.reduce((s,t)=>s+getTaskProgress(t),0)/total):0;
    return {total, assigned, unassigned, pending, inProgress, completed, active, overdue, avgProgress};
  },[globalFilteredTasks, dashNow]);

  /* Needs Manager Attention (section 4) */
  const withoutCategoryCount = useMemo(()=>globalFilteredTasks.filter(t=>!getTaskCategoryId(t)&&!getText(t.category_name,"")).length, [globalFilteredTasks]);
  const stalledPendingCount = useMemo(()=>globalFilteredTasks.filter(t=>{
    if(normalizeStatus(t.status)!=="pending"||!t.created_at) return false;
    const d=new Date(t.created_at); if(Number.isNaN(d.getTime())) return false;
    return (dashNow-d)/86400000 > 3;
  }).length, [globalFilteredTasks, dashNow]);
  const pendingApprovalsCount = useMemo(()=>approvalQueue.filter(r=>{
    const s=String(r.status_code||"").toUpperCase();
    return s.includes("PENDING")||s.includes("WAITING");
  }).length, [approvalQueue]);

  /* My Team + Team Workload (sections 5–6) — the SAME globalFilteredTasks
     the KPI cards above already use, so a member's numbers and the KPI
     totals are always traceable back to one another (never a second,
     hidden data scope). `active` (pending + in_progress + any other
     non-terminal status) is the primary current-workload number; the
     historical assigned/completed counts are kept alongside it, not
     replaced. */
  const buildOwnerStats = (mTasks) => {
    const pending = mTasks.filter(t=>normalizeStatus(t.status)==="pending").length;
    const inProgress = mTasks.filter(t=>["in_progress","open"].includes(normalizeStatus(t.status))).length;
    const completed = mTasks.filter(t=>isDoneStatus(normalizeStatus(t.status))).length;
    const overdue = mTasks.filter(t=>isOverdueTask(t,dashNow)).length;
    const assigned = mTasks.length;
    const active = mTasks.filter(t=>!isDeadStatus(normalizeStatus(t.status))).length;
    const completionRate = assigned ? Math.round(completed/assigned*100) : null;
    const workloadLevel = workloadLevelFor(active);
    // SLA % (NEW) -- same getDueStatus()==="jira-success" predicate the
    // team-wide perfMetrics.slaCompliance already uses (line ~977), just
    // applied to this member's own task subset instead of the whole view.
    const withDue = mTasks.filter(t=>t.due_at);
    const slaPct = withDue.length ? Math.round(withDue.filter(t=>getDueStatus(t).cls==="jira-success").length/withDue.length*100) : null;
    return { assigned, active, pending, inProgress, completed, overdue, completionRate, slaPct, workloadLevel, workload:WORKLOAD_LEVELS[workloadLevel] };
  };

  // Same idea as buildOwnerStats above, pulled out of the statusBuckets /
  // tatBreakdown / trendData useMemos below so the User Performance Preview
  // popup's own time filter (previewFilteredTasks, §NEW further down) can
  // compute identical shapes over its own task list without duplicating the
  // bucketing / date-range math. The page-wide versions just call these with
  // globalFilteredTasks; behavior for them is unchanged.
  const buildStatusBuckets = (list) => {
    const b={assigned:0,pending:0,in_progress:0,completed:0,overdue:0};
    list.forEach(t=>{
      const s=normalizeStatus(t.status);
      if(isDoneStatus(s)) b.completed++;
      else if(s==="pending") b.pending++;
      else if(["in_progress","open"].includes(s)) b.in_progress++;
      else if(s==="assigned") b.assigned++;
      if(isOverdueTask(t,dashNow)) b.overdue++;
    });
    return b;
  };
  const buildTatBreakdown = (list) => {
    const b={overdue:0,duesoon:0,ontrack:0,completed:0,muted:0};
    list.forEach(t=>{ const badge=getDueBadge(t,dashNow); b[badge.cls]=(b[badge.cls]||0)+1; });
    const withinTat = b.ontrack+b.completed;
    const atRisk = b.duesoon;
    const overdue = b.overdue;
    const total = list.length; // percentages are of the current total tasks in view, not just those with a due date
    const pct = n => total ? Math.round((n/total)*100) : 0;
    return { withinTat, atRisk, overdue, noDueDate: b.muted, total, withinTatPct: pct(withinTat), atRiskPct: pct(atRisk), overduePct: pct(overdue) };
  };
  const buildTrendData = (list, granularity) => {
    const n = granularity==="daily" ? 14 : granularity==="monthly" ? 6 : 8;
    const buckets=[];
    for(let i=n-1;i>=0;i--){
      let from,to,label;
      if(granularity==="daily"){
        const d=new Date(dashNow); d.setDate(d.getDate()-i);
        from=startOfDay(d); to=endOfDay(d); label=d.toLocaleDateString([],{day:"2-digit",month:"short"});
      } else if(granularity==="monthly"){
        const d=new Date(dashNow.getFullYear(), dashNow.getMonth()-i, 1);
        from=startOfDay(d); to=endOfDay(new Date(d.getFullYear(),d.getMonth()+1,0)); label=d.toLocaleDateString([],{month:"short",year:"2-digit"});
      } else {
        const to0=new Date(dashNow); to0.setDate(to0.getDate()-i*7);
        const from0=new Date(to0); from0.setDate(from0.getDate()-6);
        from=startOfDay(from0); to=endOfDay(to0); label=from0.toLocaleDateString([],{day:"2-digit",month:"short"});
      }
      buckets.push({from,to,label});
    }
    const inBucket=(v,b)=>{ if(!v) return false; const d=new Date(v); return !Number.isNaN(d.getTime())&&d>=b.from&&d<=b.to; };
    const created = buckets.map(b=>list.filter(t=>inBucket(t.created_at,b)).length);
    const completed = buckets.map(b=>list.filter(t=>isDoneStatus(normalizeStatus(t.status))&&inBucket(t.updated_at||t.created_at,b)).length);
    const pending = buckets.map(b=>list.filter(t=>normalizeStatus(t.status)==="pending"&&inBucket(t.created_at,b)).length);
    const max = Math.max(1, ...created, ...completed, ...pending);
    const createdTotal = created.reduce((s,v)=>s+v,0);
    const completedTotal = completed.reduce((s,v)=>s+v,0);
    const pendingTotal = pending.reduce((s,v)=>s+v,0);
    const total = createdTotal+completedTotal+pendingTotal;
    return { labels:buckets.map(b=>b.label), created, completed, pending, max, total, createdTotal, completedTotal, pendingTotal, hasData: total>0, sparse: total>0 && total<6 };
  };

  const memberStatsList = useMemo(()=>{
    return myTeamMembers.map(m=>{
      const mid = String(getIdValue(m));
      const mTasks = mid ? globalFilteredTasks.filter(t=>getAssigneeId(t)===mid) : [];
      return { id:mid, name:getText(m,"Member"), role:getMemberRole(m), ...buildOwnerStats(mTasks) };
    });
  },[myTeamMembers, globalFilteredTasks, dashNow]);

  // Traceability rows: every task in globalFilteredTasks whose owner isn't
  // one of myTeamMembers, split into the two DISTINCT situations a manager
  // needs told apart (§5) — a task with no owner at all is genuinely
  // "Unassigned"; a task that already has an owner reference (id or name)
  // which just isn't this manager's tracked roster is "Outside Tracked
  // Team", never mislabeled as unassigned. Together with every real
  // member's `assigned`, these two buckets always sum back to
  // `dashboardStats.total` — nothing is silently dropped. Neither is a
  // person, neither counts toward "Team Members".
  const hasOwnerRef = t => !!(getAssigneeId(t) || getText(t.assigned_to_name,""));
  const noOwnerStats = useMemo(()=>{
    const mTasks = globalFilteredTasks.filter(t=>!hasOwnerRef(t));
    return { id:"__unassigned__", name:"Unassigned", role:"—", isNoOwner:true, ...buildOwnerStats(mTasks) };
  },[globalFilteredTasks, dashNow]);
  const outsideTeamStats = useMemo(()=>{
    const memberIds = new Set(myTeamMembers.map(m=>String(getIdValue(m))));
    const mTasks = globalFilteredTasks.filter(t=>{
      if(!hasOwnerRef(t)) return false;
      const av = getAssigneeId(t);
      return !av || !memberIds.has(av);
    });
    return { id:"__outside__", name:"Outside Tracked Team", role:"—", isOutsideTeam:true, ...buildOwnerStats(mTasks) };
  },[globalFilteredTasks, myTeamMembers, dashNow]);

  // teamFiltered stays gMember-aware -- it's what the Generate Report
  // bundle deliberately narrows to "just the selected member" (an earlier,
  // explicit request). But the LIVE on-screen My Team grid and User
  // Performance Report table must never collapse down to one card just
  // because a member was clicked for drill-down -- that would hide the
  // rest of the team while the manager is comparing one person against
  // everyone else. So the roster those two render from (teamRoster) is
  // narrowed by search + role only, never by gMember; the selected card is
  // still highlighted via .md-myteam-card-selected, just not the only one
  // left on screen.
  const teamFiltered = useMemo(()=>{
    const q=teamSearch.trim().toLowerCase();
    return memberStatsList.filter(m=>{
      if(gRole!=="ALL"&&m.role!==gRole) return false;
      if(gMember!=="ALL"&&m.id!==gMember) return false;
      if(!q) return true;
      return m.name.toLowerCase().includes(q)||m.role.toLowerCase().includes(q);
    });
  },[memberStatsList, gRole, gMember, teamSearch]);

  const teamRoster = useMemo(()=>{
    const q=teamSearch.trim().toLowerCase();
    return memberStatsList.filter(m=>{
      if(gRole!=="ALL"&&m.role!==gRole) return false;
      if(!q) return true;
      return m.name.toLowerCase().includes(q)||m.role.toLowerCase().includes(q);
    });
  },[memberStatsList, gRole, teamSearch]);

  const teamVisible = teamShowAll ? teamRoster : teamRoster.slice(0,8);

  // User Performance Report table sort (NEW) -- a separate derived array so
  // sorting this one table never reorders the My Team cards above (which
  // keep reading teamRoster/teamVisible exactly as before).
  const workloadTableRows = useMemo(()=>{
    const rows = [...teamRoster];
    if(workloadSort==="completion") rows.sort((a,b)=>(b.completionRate??-1)-(a.completionRate??-1));
    else if(workloadSort==="overdue") rows.sort((a,b)=>b.overdue-a.overdue);
    else if(workloadSort==="sla") rows.sort((a,b)=>(a.slaPct??101)-(b.slaPct??101));
    else rows.sort((a,b)=>b.assigned-a.assigned); // "tasks" (default)
    return rows;
  },[teamRoster, workloadSort]);

  // Pagination (§9) -- the outside-team/unassigned traceability rows stay
  // appended after every page rather than paginated themselves, since they
  // aren't real team members and there's always at most one of each.
  const workloadTotalPages = Math.max(1, Math.ceil(workloadTableRows.length/workloadRowsPerPage));
  const workloadPageClamped = Math.min(workloadPage, workloadTotalPages);
  const workloadPageData = workloadTableRows.slice((workloadPageClamped-1)*workloadRowsPerPage, workloadPageClamped*workloadRowsPerPage);

  // "Team Exceptions" — PEOPLE/workload issues only (never task-level
  // counts like Missing Category/Long Pending/Unassigned, which live
  // exclusively in "Needs Your Attention" now, so the two sections stop
  // repeating each other). Every value is a simple count over
  // memberStatsList/globalFilteredTasks, never a new business rule.
  const teamExceptions = useMemo(()=>{
    const overdueOwners = memberStatsList.filter(m=>m.overdue>0).length;
    const slaRiskOwners = new Set(
      globalFilteredTasks
        .filter(t=>{
          if(!t.due_at||isDeadStatus(normalizeStatus(t.status))) return false;
          const due=new Date(t.due_at); if(Number.isNaN(due.getTime())) return false;
          const hrs=(due-dashNow)/3600000;
          return hrs>0 && hrs<=24;
        })
        .map(getAssigneeId)
        .filter(av=>myTeamMembers.some(m=>String(getIdValue(m))===av))
    ).size;
    const membersWithActive = memberStatsList.filter(m=>m.active>0).length;
    const membersWithNoActive = memberStatsList.filter(m=>m.active===0).length;
    const stalledOwners = new Set(
      globalFilteredTasks
        .filter(t=>{
          if(normalizeStatus(t.status)!=="pending"||!t.created_at) return false;
          const d=new Date(t.created_at); if(Number.isNaN(d.getTime())) return false;
          return (dashNow-d)/86400000>3;
        })
        .map(getAssigneeId)
        .filter(av=>myTeamMembers.some(m=>String(getIdValue(m))===av))
    ).size;
    return { overdueOwners, slaRiskOwners, membersWithActive, membersWithNoActive, stalledOwners };
  },[memberStatsList, globalFilteredTasks, myTeamMembers, dashNow]);

  /* Needs Manager Attention (section 4) — richer title/subtitle rows now
     that owner counts (teamExceptions) are available, still driven only by
     dashboardStats/withoutCategoryCount/stalledPendingCount/pendingApprovalsCount
     already computed above; clicking reuses the SAME existing setStatusF/
     scrollToTaskTable/navigate already wired to real behavior. */
  // Ticket-level issues ONLY (people/workload issues live in Team
  // Exceptions instead — see that memo's own comment). Ordered by
  // managerial priority: work outside the tracked roster first (the
  // manager can't even see who owns it), then true unassigned, then
  // overdue, then stale pending, then data-quality.
  const missingCategoryPct = dashboardStats.total ? Math.round(withoutCategoryCount/dashboardStats.total*100) : 0;
  const attentionItems = useMemo(()=>{
    const items=[];
    if(outsideTeamStats.active>0) items.push({key:"outside-team",tone:"critical",Icon:FaTriangleExclamation,
      title:`${outsideTeamStats.active} Active Ticket${outsideTeamStats.active===1?"":"s"} Outside Tracked Team`,
      sub:`${outsideTeamStats.pending} Pending • ${outsideTeamStats.inProgress} In Progress`,
      cta:"Review →", action:openComplaints()});
    if(noOwnerStats.assigned>0) items.push({key:"unassigned",tone:"warning",Icon:FaFlag,
      title:`${noOwnerStats.assigned} Unassigned Ticket${noOwnerStats.assigned===1?"":"s"}`,
      sub:"Needs an owner", cta:"View →", action:openComplaints()});
    if(dashboardStats.overdue>0) items.push({key:"overdue",tone:"critical",Icon:FaCircleExclamation,
      title:`${dashboardStats.overdue} Overdue Ticket${dashboardStats.overdue===1?"":"s"}`,
      sub:teamExceptions.overdueOwners>0?`Across ${teamExceptions.overdueOwners} team member${teamExceptions.overdueOwners===1?"":"s"} — immediate action required`:"Immediate action required",
      cta:"View →", action:openComplaints()});
    if(stalledPendingCount>0) items.push({key:"stalled",tone:"warning",Icon:FaClock,
      title:`${stalledPendingCount} Ticket${stalledPendingCount===1?"":"s"} Pending > 3 Days`,
      sub:teamExceptions.stalledOwners>0?`Across ${teamExceptions.stalledOwners} team member${teamExceptions.stalledOwners===1?"":"s"} — stale workload`:"Stale workload",
      cta:"Review →", action:openComplaints("pending")});
    // Lowest-urgency item: a data-hygiene note, not a blocker or a stale-work
    // risk, so it's the one item classified "info" rather than "warning" --
    // giving Needs Your Attention all three severity levels (critical/
    // warning/info) instead of only two.
    if(withoutCategoryCount>0) items.push({key:"category",tone:"info",Icon:FaLayerGroup,
      title:`${withoutCategoryCount} Ticket${withoutCategoryCount===1?"":"s"} Missing Category`,
      sub:`${missingCategoryPct}% of tickets unclassified — review required`, cta:"Review →", action:openComplaints()});
    return items;
  },[dashboardStats, withoutCategoryCount, stalledPendingCount, teamExceptions, outsideTeamStats, noOwnerStats, missingCategoryPct]);

  /* Team Status + Team Performance (section 7) */
  const statusBuckets = useMemo(()=>buildStatusBuckets(globalFilteredTasks),[globalFilteredTasks, dashNow]);
  const statusBucketMax = Math.max(1, ...Object.values(statusBuckets));

  /* User-wise drill-down (NEW). `gMember` already narrows
     globalFilteredTasks (and everything derived from it above -- KPIs,
     statusBuckets, teamExceptions, trendData) down to one person, so the
     "selected user" and their task list need no separate filtering logic
     of their own: they're just that same already-scoped data, looked up
     and labeled. */
  const selectedMemberStats = useMemo(()=>
    gMember==="ALL" ? null : (memberStatsList.find(m=>m.id===gMember)||null)
  ,[gMember, memberStatsList]);
  const selectedUserTasks = gMember==="ALL" ? [] : globalFilteredTasks;

  // The popup's own "whose report is this" -- driven by previewMemberId,
  // NOT gMember, so previewUserDetails (both My Team cards and the User
  // Performance Report table's "View") can open it without touching the
  // page-wide member filter.
  const popupMemberStats = useMemo(()=>
    previewMemberId==null ? null : (memberStatsList.find(m=>m.id===previewMemberId)||null)
  ,[previewMemberId, memberStatsList]);

  // TAT/SLA breakdown -- reuses the existing getDueBadge() classification
  // (already driving the task table's own Due/SLA column) over whichever
  // scope is currently selected (team-wide or one user), never a new TAT
  // calculation.
  const tatBreakdown = useMemo(()=>buildTatBreakdown(globalFilteredTasks),[globalFilteredTasks, dashNow]);

  // Ticket Status donut -- MUTUALLY EXCLUSIVE workflow statuses only
  // (Pending + In Progress + Completed always sum to the total shown at
  // its center). Overdue is deliberately excluded here: it's a
  // cross-cutting due-date flag that can overlap any of the three above
  // (an in-progress task can also be overdue), so plotting it as a 4th
  // donut slice would double-count tasks and make the slices not sum to
  // the whole. Overdue lives exclusively in the TAT/SLA summary instead.
  const statusChartTotal = statusBuckets.pending+statusBuckets.in_progress+statusBuckets.completed;
  const statusChartRows = useMemo(()=>{
    const rows=[
      {key:"pending", label:"Pending", val:statusBuckets.pending, color:"#D97706"},
      {key:"in_progress", label:"In Progress", val:statusBuckets.in_progress, color:"#2563EB"},
      {key:"completed", label:"Completed", val:statusBuckets.completed, color:"#16A34A"},
    ];
    return rows.map(r=>({...r, pct: statusChartTotal ? Math.round((r.val/statusChartTotal)*1000)/10 : 0}));
  },[statusBuckets, statusChartTotal]);
  const statusChartData = useMemo(()=>({
    labels: statusChartRows.map(r=>r.label),
    datasets:[{
      data: statusChartRows.map(r=>r.val),
      backgroundColor: statusChartRows.map(r=>r.color),
      borderWidth:2, borderColor:"#fff",
    }],
  }),[statusChartRows]);
  const statusChartHasData = statusChartTotal>0;
  const statusChartOptions = {
    responsive:true, maintainAspectRatio:false, cutout:"70%", devicePixelRatio:2,
    plugins:{
      legend:{ display:false }, // custom legend row rendered below the donut instead (with count + %)
      tooltip:{ callbacks:{ label:ctx=>{
        const pct = statusChartTotal ? Math.round((ctx.raw/statusChartTotal)*1000)/10 : 0;
        return ` ${ctx.label}: ${ctx.raw} (${pct}%)`;
      } } },
    },
  };

  const perfMetrics = useMemo(()=>{
    const total=dashboardStats.total;
    const completionRate = total? Math.round(dashboardStats.completed/total*100):0;
    const overdueRate = total? Math.round(dashboardStats.overdue/total*100):0;
    const withDue = globalFilteredTasks.filter(t=>t.due_at);
    const slaCompliance = withDue.length ? Math.round(withDue.filter(t=>getDueStatus(t).cls==="jira-success").length/withDue.length*100) : null;
    return { completionRate, avgProgress: dashboardStats.avgProgress, slaCompliance, overdueRate };
  },[dashboardStats, globalFilteredTasks]);

  /* Team Performance Trend (section 9) */
  const trendData = useMemo(()=>buildTrendData(globalFilteredTasks, trendGranularity),[globalFilteredTasks, trendGranularity, dashNow]);

  // Grouped bar chart config for Team Performance Trend -- same chart.js
  // instance already registered above (Doughnut's ArcElement + this
  // section's BarElement/CategoryScale/LinearScale), reusing trendData's
  // labels/created/completed/pending as-is. Colors match the KPI/legend
  // convention exactly: Created blue, Completed green, Pending amber.
  const trendChartData = useMemo(()=>({
    labels: trendData.labels,
    datasets: [
      { label:"Created",   data:trendData.created,   backgroundColor:"#2563EB", borderRadius:4, maxBarThickness:20, categoryPercentage:0.7, barPercentage:0.85 },
      { label:"Completed", data:trendData.completed, backgroundColor:"#16A34A", borderRadius:4, maxBarThickness:20, categoryPercentage:0.7, barPercentage:0.85 },
      { label:"Pending",   data:trendData.pending,   backgroundColor:"#D97706", borderRadius:4, maxBarThickness:20, categoryPercentage:0.7, barPercentage:0.85 },
    ],
  }),[trendData]);
  const trendChartOptions = {
    responsive:true, maintainAspectRatio:false, devicePixelRatio:2,
    interaction:{mode:"index", intersect:false},
    plugins:{
      legend:{ display:false }, // legend is rendered as the existing compact dot-legend row above the chart instead
      tooltip:{ callbacks:{ label:ctx=>` ${ctx.dataset.label}: ${ctx.raw}` } },
    },
    scales:{
      x:{ grid:{display:false}, ticks:{font:{size:10.5}, color:"#78716C"} },
      y:{ beginAtZero:true, ticks:{precision:0, font:{size:10.5}, color:"#78716C"}, grid:{color:"#F3F4F6"} },
    },
  };
  // Date-range context label (§6) -- purely a label over the same
  // trendGranularity/trendData.labels already driving the chart, e.g.
  // "Daily • 18 Aug – 31 Aug".
  const trendRangeLabel = trendData.labels.length
    ? `${titleCase(trendGranularity)} • ${trendData.labels[0]}${trendData.labels.length>1?` – ${trendData.labels[trendData.labels.length-1]}`:""}`
    : "";

  // NEW: User Performance Preview popup (§9B) — SLA Performance bar chart.
  // Same tatBreakdown counts the tiles above it already render, just also
  // plotted as a compact bar chart per the popup's chart spec.
  const slaBarChartData = useMemo(()=>({
    labels:["Within SLA","Near Risk","Overdue"],
    datasets:[{
      data:[tatBreakdown.withinTat, tatBreakdown.atRisk, tatBreakdown.overdue],
      backgroundColor:["#16A34A","#D97706","#DC2626"],
      borderRadius:4, maxBarThickness:34,
    }],
  }),[tatBreakdown]);
  const slaBarChartOptions = {
    responsive:true, maintainAspectRatio:false, devicePixelRatio:2,
    plugins:{ legend:{display:false}, tooltip:{ callbacks:{ label:ctx=>` ${ctx.raw} ticket${ctx.raw===1?"":"s"}` } } },
    scales:{
      x:{ grid:{display:false}, ticks:{font:{size:10.5}, color:"#78716C"} },
      y:{ beginAtZero:true, ticks:{precision:0, font:{size:10.5}, color:"#78716C"}, grid:{color:"#F3F4F6"} },
    },
  };

  // NEW: User Performance Preview popup (§9C) — Productivity Trend line
  // chart ("task completion over time"). Reuses trendData.completed exactly
  // as already computed above for Team Performance Trend; when gMember is
  // set (the popup only ever opens for one selected member), trendData is
  // already scoped to that member's own tasks, so this is the same number
  // already shown elsewhere, just drawn as a line instead of grouped bars.
  const productivityTrendChartData = useMemo(()=>({
    labels:trendData.labels,
    datasets:[{
      label:"Completed", data:trendData.completed,
      borderColor:"#16A34A", backgroundColor:"rgba(22,163,74,0.12)",
      pointBackgroundColor:"#16A34A", pointRadius:3, pointHoverRadius:5,
      tension:0.35, fill:true,
    }],
  }),[trendData]);
  const productivityTrendChartOptions = {
    responsive:true, maintainAspectRatio:false, devicePixelRatio:2,
    plugins:{ legend:{display:false}, tooltip:{ callbacks:{ label:ctx=>` Completed: ${ctx.raw}` } } },
    scales:{
      x:{ grid:{display:false}, ticks:{font:{size:10}, color:"#78716C", maxRotation:0, autoSkip:true} },
      y:{ beginAtZero:true, ticks:{precision:0, font:{size:10}, color:"#78716C"}, grid:{color:"#F3F4F6"} },
    },
  };

  // NEW: the popup's own time filter (previewPeriod/previewCustomFrom/
  // previewCustomTo, declared with showUserPreview above) needs a fully
  // independent task list and KPI/status/TAT/trend breakdown, so the rest
  // of the dashboard behind the popup (globalFilteredTasks/dashboardStats/
  // statusBuckets/tatBreakdown/trendData, all still tied to gPeriod) never
  // changes while it's open. Mirrors globalFilteredTasks' own member/role/
  // status/priority/category predicates exactly, just fixed to the one
  // already-selected member (selectedMemberStats) and swapping gPeriod for
  // previewPeriod.
  const previewFilteredTasks = useMemo(()=>{
    if(!popupMemberStats) return [];
    if(gRole!=="ALL" && popupMemberStats.role!==gRole) return [];
    const [from,to] = periodRange(previewPeriod, previewCustomFrom, previewCustomTo, dashNow);
    return tasks.filter(t=>{
      if(getAssigneeId(t)!==popupMemberStats.id) return false;
      if(from||to){
        const c = t.created_at ? new Date(t.created_at) : null;
        if(!c||Number.isNaN(c.getTime())) return false;
        if(from&&c<from) return false;
        if(to&&c>to) return false;
      }
      if(gStatus!=="ALL" && normalizeStatus(t.status)!==gStatus) return false;
      if(gPriority!=="ALL" && String(t.priority||"").toLowerCase()!==gPriority) return false;
      if(gCategory!=="ALL" && getTaskCategoryId(t)!==gCategory) return false;
      return true;
    });
  },[tasks, popupMemberStats, gRole, gStatus, gPriority, gCategory, previewPeriod, previewCustomFrom, previewCustomTo, dashNow]);

  const previewStats = useMemo(()=>buildOwnerStats(previewFilteredTasks),[previewFilteredTasks]);

  const previewStatusBuckets = useMemo(()=>buildStatusBuckets(previewFilteredTasks),[previewFilteredTasks, dashNow]);
  const previewStatusTotal = previewStatusBuckets.pending+previewStatusBuckets.in_progress+previewStatusBuckets.completed;
  const previewStatusRows = useMemo(()=>{
    const rows=[
      {key:"pending", label:"Pending", val:previewStatusBuckets.pending, color:"#D97706"},
      {key:"in_progress", label:"In Progress", val:previewStatusBuckets.in_progress, color:"#2563EB"},
      {key:"completed", label:"Completed", val:previewStatusBuckets.completed, color:"#16A34A"},
    ];
    return rows.map(r=>({...r, pct: previewStatusTotal ? Math.round((r.val/previewStatusTotal)*1000)/10 : 0}));
  },[previewStatusBuckets, previewStatusTotal]);
  const previewStatusChartData = useMemo(()=>({
    labels: previewStatusRows.map(r=>r.label),
    datasets:[{
      data: previewStatusRows.map(r=>r.val),
      backgroundColor: previewStatusRows.map(r=>r.color),
      borderWidth:2, borderColor:"#fff",
    }],
  }),[previewStatusRows]);
  const previewStatusHasData = previewStatusTotal>0;
  const previewStatusChartOptions = {
    responsive:true, maintainAspectRatio:false, cutout:"70%", devicePixelRatio:2,
    plugins:{
      legend:{ display:false },
      tooltip:{ callbacks:{ label:ctx=>{
        const pct = previewStatusTotal ? Math.round((ctx.raw/previewStatusTotal)*1000)/10 : 0;
        return ` ${ctx.label}: ${ctx.raw} (${pct}%)`;
      } } },
    },
  };

  const previewTatBreakdown = useMemo(()=>buildTatBreakdown(previewFilteredTasks),[previewFilteredTasks, dashNow]);
  const previewSlaBarChartData = useMemo(()=>({
    labels:["Within SLA","Near Risk","Overdue"],
    datasets:[{
      data:[previewTatBreakdown.withinTat, previewTatBreakdown.atRisk, previewTatBreakdown.overdue],
      backgroundColor:["#16A34A","#D97706","#DC2626"],
      borderRadius:4, maxBarThickness:34,
    }],
  }),[previewTatBreakdown]);

  // Reuses trendGranularity (the same daily/weekly/monthly toggle Team
  // Performance Trend already exposes) but buckets previewFilteredTasks
  // instead of globalFilteredTasks, so the popup's own line chart reflects
  // previewPeriod, not the page-wide gPeriod.
  const previewTrendData = useMemo(()=>buildTrendData(previewFilteredTasks, trendGranularity),[previewFilteredTasks, trendGranularity, dashNow]);
  const previewTrendChartData = useMemo(()=>({
    labels:previewTrendData.labels,
    datasets:[{
      label:"Completed", data:previewTrendData.completed,
      borderColor:"#16A34A", backgroundColor:"rgba(22,163,74,0.12)",
      pointBackgroundColor:"#16A34A", pointRadius:3, pointHoverRadius:5,
      tension:0.35, fill:true,
    }],
  }),[previewTrendData]);

  const previewPeriodLabel = PERIOD_LABELS[previewPeriod]||"All Time";

  /* Open Ticket Aging (§10) — 0-1 / 2-3 / 4-7 / 8-14 / 15+ day buckets over
     still-open (non-terminal) tasks' created_at, mirroring the same
     age-bucketing pattern already used elsewhere in the app (e.g.
     DataAnalystDashboard's agingBuckets) over the already-loaded
     globalFilteredTasks. No new API call. */
  const agingBuckets = useMemo(()=>{
    const buckets=[
      {key:"0_1d",   label:"0–1 Days",  min:0,  max:2,       count:0},
      {key:"2_3d",   label:"2–3 Days",  min:2,  max:4,       count:0},
      {key:"4_7d",   label:"4–7 Days",  min:4,  max:8,       count:0},
      {key:"8_14d",  label:"8–14 Days", min:8,  max:15,      count:0},
      {key:"15d_plus",label:"15+ Days", min:15, max:Infinity,count:0},
    ];
    globalFilteredTasks.forEach(t=>{
      if(isDeadStatus(normalizeStatus(t.status))||!t.created_at) return;
      const created=new Date(t.created_at); if(Number.isNaN(created.getTime())) return;
      const ageDays=(dashNow-created)/86400000;
      const bucket=buckets.find(b=>ageDays>=b.min&&ageDays<b.max)||buckets[buckets.length-1];
      bucket.count++;
    });
    return buckets;
  },[globalFilteredTasks, dashNow]);
  const agingTotal = agingBuckets.reduce((s,b)=>s+b.count,0);

  /* Priority Risk Analysis (§11) — groups globalFilteredTasks by the same
     getPriorityInfo() tone convention already driving every priority badge
     on this page (table rows, user-detail table); tasks with no priority
     value are counted separately as "Unspecified" rather than guessed.
     Each bucket also carries its own overdue count (isOverdueTask, already
     used everywhere else on this page) so the widget can answer "what
     needs attention" instead of just showing a decorative distribution. */
  const buildPriorityBuckets = (list) => {
    const counts = {critical:0, high:0, medium:0, low:0};
    const overdueCounts = {critical:0, high:0, medium:0, low:0};
    let unspecified = 0;
    list.forEach(t=>{
      const info = getPriorityInfo(t.priority);
      if(!info) { unspecified++; return; }
      counts[info.cls] = (counts[info.cls]||0)+1;
      if(isOverdueTask(t,dashNow)) overdueCounts[info.cls] = (overdueCounts[info.cls]||0)+1;
    });
    const rows = ["critical","high","medium","low"].map(key=>({
      key, label:titleCase(key), val:counts[key], overdue:overdueCounts[key], color:PRIORITY_COLORS[key],
    }));
    return { rows, unspecified, total: list.length };
  };
  const priorityBuckets = useMemo(()=>buildPriorityBuckets(globalFilteredTasks),[globalFilteredTasks, dashNow]);

  /* Category Analysis (NEW) — same category_name/getTaskCategoryId fields
     withoutCategoryCount already reads, grouped and ranked; top 6 shown,
     "Uncategorized" tasks excluded here since they're already surfaced by
     the Data Quality section below. */
  const buildCategoryBuckets = (list) => {
    const catNameById = new Map(categories.map(c=>[String(getIdValue(c)), getText(c,"")]));
    const m = new Map();
    list.forEach(t=>{
      const name = getText(t.category_name,"") || catNameById.get(getTaskCategoryId(t)) || "";
      if(!name) return;
      m.set(name,(m.get(name)||0)+1);
    });
    const all = [...m.entries()].map(([label,val])=>({label,val})).sort((a,b)=>b.val-a.val);
    const rows = all.slice(0,6);
    const max = Math.max(1, ...all.map(r=>r.val));
    return { rows, all, max };
  };
  const categoryBuckets = useMemo(()=>buildCategoryBuckets(globalFilteredTasks),[globalFilteredTasks, categories]);

  /* Data Quality (NEW) — withoutCategoryCount already exists above (§4);
     missingPriorityCount is the same shape of check over the existing
     `priority` field, no new business rule. */
  const missingPriorityCount = useMemo(()=>globalFilteredTasks.filter(t=>!getPriorityInfo(t.priority)).length, [globalFilteredTasks]);

  // Data Quality Score (NEW) — % of tasks in view with BOTH a category and a
  // priority set; counts the union (a task missing both only counts once)
  // over the same globalFilteredTasks already used for withoutCategoryCount/
  // missingPriorityCount above, so it can never disagree with those two
  // numbers. Display-only aggregate, not a new business rule.
  const dataQualityScore = useMemo(()=>{
    const total = globalFilteredTasks.length;
    if(!total) return { pct:100, issues:0, total:0 };
    const issues = globalFilteredTasks.filter(t=>(!getTaskCategoryId(t)&&!getText(t.category_name,"")) || !getPriorityInfo(t.priority)).length;
    return { pct: Math.round(((total-issues)/total)*100), issues, total };
  },[globalFilteredTasks]);

  /* Team Workload / Capacity (§5/§6) — THE single canonical per-member
     workload view, replacing what used to be three separate renderings
     (Team Exceptions' people-count tiles, this section's old 3-bucket
     "Team Workload Balance" grouping, and the User Performance Report's
     own Workload column standing in for it). Sourced from the exact same
     memberStatsList every other section already reads; sorted by current
     active load (most loaded first) since that's the operational question
     this panel answers. capacityLevelFor is the display-only LOW/NORMAL/
     HIGH/OVERLOADED read of the same `active` count workloadLevelFor
     already buckets elsewhere — no new underlying data. */
  const capacityRows = useMemo(()=>{
    return [...memberStatsList]
      .filter(m=>m.assigned>0 || m.active>0)
      .sort((a,b)=>b.active-a.active)
      .map(m=>({ ...m, capacity: capacityLevelFor(m.active) }));
  },[memberStatsList]);
  const capacityMax = Math.max(1, ...memberStatsList.map(m=>m.active));
  const capacityVisible = capacityShowAll ? capacityRows : capacityRows.slice(0,6);

  /* User Performance Ranking (NEW) — ranks memberStatsList (already has
     completed/completionRate/overdue from buildOwnerStats) by completed
     tasks; purely a display sort/label pass, not a new scoring rule. */
  const performanceRanking = useMemo(()=>{
    return [...memberStatsList]
      .filter(m=>m.assigned>0)
      .sort((a,b)=> b.completed-a.completed || (b.completionRate||0)-(a.completionRate||0))
      .slice(0,8)
      .map((m,i)=>({
        ...m, rank:i+1,
        tag: i===0 && m.completed>0 ? "top" : m.workloadLevel==="high" ? "high-load" : (m.overdue>0 && (m.completionRate||0)<50) ? "attention" : null,
      }));
  },[memberStatsList]);

  // User Performance Graph (NEW, §4C) -- "Users vs Completed Tickets" bar
  // chart over the exact same performanceRanking rows the table below it
  // already lists; same chart.js Bar/BarElement already registered above.
  const userPerfChartData = useMemo(()=>({
    labels: performanceRanking.map(m=>m.name),
    datasets:[{
      label:"Completed Tickets", data:performanceRanking.map(m=>m.completed),
      backgroundColor:"#16A34A", borderRadius:4, maxBarThickness:18,
    }],
  }),[performanceRanking]);
  const userPerfChartOptions = {
    indexAxis:"y", responsive:true, maintainAspectRatio:false, devicePixelRatio:2,
    // extra right-side padding so the on-bar value labels (barValueLabelPlugin) never clip off the canvas edge
    layout:{ padding:{ right:22 } },
    plugins:{ legend:{display:false}, tooltip:{ callbacks:{ label:ctx=>` ${ctx.raw} completed` } } },
    scales:{
      x:{ beginAtZero:true, ticks:{precision:0, font:{size:10.5}, color:"#78716C"}, grid:{color:"#F3F4F6"} },
      y:{ grid:{display:false}, ticks:{font:{size:11}, color:"#57534E"} },
    },
  };

  /* Complaints Overview (section 10) — tasks whose own task_type fields
     textually indicate IT-support/complaint, same fields ComplaintsListPage
     already reads (task_type_name/task_type) off this same /tasks response.
     Purely a display-layer match over already-loaded data; if nothing in
     the loaded tasks is tagged that way, this safely stays at 0 rather than
     guessing. */
  const complaintsSummary = useMemo(()=>{
    const complaintTasks = tasks.filter(t=>{
      const tt=(getText(t.task_type_name,"")+" "+getText(t.task_type,"")).toLowerCase();
      return tt.includes("complaint")||tt.includes("it support")||tt.includes("it_support");
    });
    const open = complaintTasks.filter(t=>!isDeadStatus(normalizeStatus(t.status))).length;
    const pending = complaintTasks.filter(t=>normalizeStatus(t.status)==="pending").length;
    const completed = complaintTasks.filter(t=>isDoneStatus(normalizeStatus(t.status))).length;
    const overdue = complaintTasks.filter(t=>isOverdueTask(t,dashNow)).length;
    return { total:complaintTasks.length, open, pending, completed, overdue };
  },[tasks, dashNow]);

  /* Recently Completed Tickets (§16) — no separate activity-log endpoint
     exists on this page, so per the fallback rule this uses the 5 most
     recently completed tasks already loaded (Ticket/Employee/Completed date),
     completed-only per the spec (never mixed with in-progress/pending
     updates the way the old "Recently Updated Tickets" list was). */
  const recentCompletedTasks = useMemo(()=>{
    return [...tasks]
      .filter(t=>isDoneStatus(normalizeStatus(t.status)) && (t.updated_at||t.created_at))
      .sort((a,b)=>new Date(b.updated_at||b.created_at||0)-new Date(a.updated_at||a.created_at||0))
      .slice(0,5);
  },[tasks]);

  const managerName = user?.name || (user?.email ? user.email.split("@")[0] : "Manager");
  // Same fallback chain getMemberRole() already uses for team members,
  // applied to the logged-in user's own record for the header (§2).
  const managerRole = [user?.role_name, user?.roleName, user?.role].map(x=>getText(x,"")).find(Boolean) || "Manager";
  const todayLabel = dashNow.toLocaleDateString([], {weekday:"long", day:"2-digit", month:"long", year:"numeric"});

  // Generate Report modal's OWN filtered view (§ "filter should only work on
  // this popup") -- same shape as globalFilteredTasks, but scoped by
  // reportPeriod/reportMember instead of gPeriod/gMember, so changing the
  // modal's own Period/Member controls never re-filters the dashboard behind
  // it. gRole/gStatus/gPriority/gCategory have no control inside the modal,
  // so they're simply inherited from whatever's set on the page.
  const reportFilteredTasks = useMemo(()=>{
    const [from,to] = periodRange(reportPeriod, reportCustomFrom, reportCustomTo, dashNow);
    return tasks.filter(t=>{
      if(from||to){
        const c = t.created_at ? new Date(t.created_at) : null;
        if(!c||Number.isNaN(c.getTime())) return false;
        if(from&&c<from) return false;
        if(to&&c>to) return false;
      }
      if(gStatus!=="ALL" && normalizeStatus(t.status)!==gStatus) return false;
      if(gPriority!=="ALL" && String(t.priority||"").toLowerCase()!==gPriority) return false;
      if(gCategory!=="ALL" && getTaskCategoryId(t)!==gCategory) return false;
      const av = getAssigneeId(t);
      if(reportMember!=="ALL" && av!==reportMember) return false;
      if(gRole!=="ALL"){
        const member = myTeamMembers.find(m=>String(getIdValue(m))===av);
        if(!member || getMemberRole(member)!==gRole) return false;
      }
      return true;
    });
  },[tasks, reportPeriod, reportCustomFrom, reportCustomTo, reportMember, gStatus, gPriority, gCategory, gRole, myTeamMembers, dashNow]);

  const reportStats = useMemo(()=>buildOwnerStats(reportFilteredTasks),[reportFilteredTasks]);

  const reportModalFilterChips = useMemo(()=>{
    const chips = [];
    if(reportPeriod!=="all") chips.push({key:"period", label:PERIOD_LABELS[reportPeriod]||titleCase(reportPeriod)});
    if(reportMember!=="ALL"){
      const m=myTeamMembers.find(x=>String(getIdValue(x))===reportMember);
      chips.push({key:"member", label:m?getText(m):"Selected Member"});
    }
    if(gRole!=="ALL") chips.push({key:"role", label:gRole});
    if(gStatus!=="ALL") chips.push({key:"status", label:STATUS_LABELS[gStatus]||titleCase(gStatus)});
    if(gPriority!=="ALL") chips.push({key:"priority", label:`${titleCase(gPriority)} Priority`});
    if(gCategory!=="ALL"){
      const c=categories.find(x=>String(getIdValue(x))===gCategory);
      chips.push({key:"category", label:c?getText(c):"Selected Category"});
    }
    if(!chips.length) chips.push({key:"all", label:"All Time • All Members • All Roles"});
    return chips;
  },[reportPeriod, reportMember, gRole, gStatus, gPriority, gCategory, myTeamMembers, categories]);

  const hasActiveReportFilters = reportPeriod!=="all"||reportMember!=="ALL";
  const resetReportFilters = () => { setReportPeriod("all"); setReportCustomFrom(""); setReportCustomTo(""); setReportMember("ALL"); };

  // Same shape as memberStatsList/teamFiltered above, just built off
  // reportFilteredTasks instead of globalFilteredTasks so each member's row
  // in the generated report reflects the modal's OWN period, not the
  // dashboard's.
  const reportMemberStatsList = useMemo(()=>{
    return myTeamMembers.map(m=>{
      const mid = String(getIdValue(m));
      const mTasks = mid ? reportFilteredTasks.filter(t=>getAssigneeId(t)===mid) : [];
      return { id:mid, name:getText(m,"Member"), role:getMemberRole(m), ...buildOwnerStats(mTasks) };
    });
  },[myTeamMembers, reportFilteredTasks]);
  const reportTeamRows = useMemo(()=>{
    return reportMemberStatsList.filter(m=>{
      if(gRole!=="ALL"&&m.role!==gRole) return false;
      if(reportMember!=="ALL"&&m.id!==reportMember) return false;
      return true;
    });
  },[reportMemberStatsList, gRole, reportMember]);

  // Generate Report (NEW) — builds one normalized "bundle", reused by both
  // the Excel and PDF export functions below. `scope` picks WHICH already-
  // computed data it reads from: "global" (default, unchanged) is the
  // page-wide gPeriod/gMember/etc-scoped dashboardStats/perfMetrics/
  // teamFiltered/globalFilteredTasks -- used by the header's "Export
  // Dashboard" button and the Reports & Insights "Export Excel" quick
  // action, neither of which opens the Generate Report modal. "report" is
  // the modal's OWN reportPeriod/reportMember-scoped reportStats/
  // reportTeamRows/reportFilteredTasks/reportModalFilterChips -- used only
  // by the modal's own Download Excel/PDF buttons, so the modal's filters
  // never leak into (or get overridden by) the page-wide ones. No new API
  // call, no new business calculation either way — every number is read
  // straight off state already computed above.
  const buildReportBundle = (scope="global") => {
    const isReport = scope==="report";
    const srcTasks = isReport ? reportFilteredTasks : globalFilteredTasks;
    const kpiSource = isReport
      ? { total:reportStats.assigned, completed:reportStats.completed, pending:reportStats.pending, inProgress:reportStats.inProgress, overdue:reportStats.overdue, completionRate:reportStats.completionRate??0, slaCompliance:reportStats.slaPct }
      : { total:dashboardStats.total, completed:dashboardStats.completed, pending:dashboardStats.pending, inProgress:dashboardStats.inProgress, overdue:dashboardStats.overdue, completionRate:perfMetrics.completionRate, slaCompliance:perfMetrics.slaCompliance };
    const teamRows = isReport ? reportTeamRows : teamFiltered;
    const filterLabels = (isReport ? reportModalFilterChips : reportFilterChips).map(c=>c.label);
    const total = kpiSource.total;
    const pct = n => total ? Math.round((n/total)*100) : 0;
    const statusBuckets = buildStatusBuckets(srcTasks);
    const priorityBuckets = buildPriorityBuckets(srcTasks);
    const categoryBuckets = buildCategoryBuckets(srcTasks);
    const tatBreakdown = buildTatBreakdown(srcTasks);
    return {
      meta: {
        managerName, generatedAt: new Date(),
        filters: filterLabels,
        totalRecords: total,
      },
      kpi: [
        {label:"Total Tickets", value:kpiSource.total},
        {label:"Completed", value:kpiSource.completed},
        {label:"Pending", value:kpiSource.pending},
        {label:"In Progress", value:kpiSource.inProgress},
        {label:"Overdue", value:kpiSource.overdue},
        {label:"Completion %", value:`${kpiSource.completionRate}%`},
        {label:"SLA Compliance", value:kpiSource.slaCompliance===null?"—":`${kpiSource.slaCompliance}%`},
      ],
      // teamRows (search + gRole + member, scoped per `scope` above) --
      // deliberately narrower than teamRoster (which the on-screen My Team/
      // User Performance Report table use, and never narrows by member):
      // selecting one member shows only that member's row in the generated
      // report, instead of every team member with zeroed-out rows, while
      // the live dashboard keeps showing everyone.
      team: teamRows.map(m=>({
        name:m.name, role:m.role, assigned:m.assigned, completed:m.completed,
        pending:m.pending, overdue:m.overdue, completionRate:m.completionRate===null?"—":`${m.completionRate}%`,
      })),
      tasks: srcTasks.map(t=>{
        const priorityInfo = getPriorityInfo(t.priority);
        return {
          id:t.id,
          title:getText(taskDisplayTitle(t),""),
          category:getText(t.category_name,"")||"Uncategorized",
          priority:priorityInfo?priorityInfo.label:"Unspecified",
          status:STATUS_LABELS[normalizeStatus(t.status)]||titleCase(normalizeStatus(t.status)),
          assignedUser:getText(t.assigned_to_name,"")||getText(t.assigned_to,"")||getText(t.assignedTo,"")||"Unassigned",
          createdDate:t.created_at?formatShortDate(t.created_at):"—",
          dueDate:t.due_at?formatShortDate(t.due_at):"—",
          slaStatus:getDueBadge(t,dashNow).label,
        };
      }),
      statusDist: [
        {label:"Assigned", value:statusBuckets.assigned},
        {label:"Pending", value:statusBuckets.pending},
        {label:"In Progress", value:statusBuckets.in_progress},
        {label:"Completed", value:statusBuckets.completed},
        {label:"Overdue", value:statusBuckets.overdue},
      ].map(r=>({...r, pct:pct(r.value)})),
      priorityDist: [
        ...priorityBuckets.rows.map(r=>({label:r.label, value:r.val, pct:priorityBuckets.total?Math.round(r.val/priorityBuckets.total*100):0})),
        ...(priorityBuckets.unspecified>0 ? [{label:"Unspecified", value:priorityBuckets.unspecified, pct:priorityBuckets.total?Math.round(priorityBuckets.unspecified/priorityBuckets.total*100):0}] : []),
      ],
      categoryDist: categoryBuckets.all.map(r=>({label:r.label, value:r.val, pct:pct(r.val)})),
      slaSummary: [
        {label:"Within SLA", value:tatBreakdown.withinTat, pct:tatBreakdown.withinTatPct},
        {label:"Near Risk", value:tatBreakdown.atRisk, pct:tatBreakdown.atRiskPct},
        {label:"Overdue", value:tatBreakdown.overdue, pct:tatBreakdown.overduePct},
      ],
    };
  };

  const downloadReportExcel = (scope="global") => {
    setReportGenerating("excel");
    try {
      const bundle = buildReportBundle(scope);
      const summarySheet = [
        {Field:"Report", Value:"Manager Ticket Performance Report"},
        {Field:"Manager", Value:bundle.meta.managerName},
        {Field:"Generated", Value:bundle.meta.generatedAt.toLocaleString()},
        {Field:"Applied Filters", Value:bundle.meta.filters.join(", ")},
        {Field:"Total Records", Value:bundle.meta.totalRecords},
      ];
      const kpiSheet = bundle.kpi.map(k=>({Metric:k.label, Value:k.value}));
      const teamSheet = bundle.team.map(m=>({
        "User Name":m.name, Role:m.role, "Assigned Tickets":m.assigned, Completed:m.completed,
        Pending:m.pending, Overdue:m.overdue, "Completion %":m.completionRate,
      }));
      const taskSheet = bundle.tasks.map(t=>({
        "Ticket ID":t.id, "Ticket Name":taskDisplayTitle(t), Category:t.category, Priority:t.priority, Status:t.status,
        "Assigned User":t.assignedUser, "Created Date":t.createdDate, "Due Date":t.dueDate, "SLA Status":t.slaStatus,
      }));
      const statusSheet = bundle.statusDist.map(r=>({Status:r.label, Count:r.value, "%":`${r.pct}%`}));
      const prioritySheet = bundle.priorityDist.map(r=>({Priority:r.label, Count:r.value, "%":`${r.pct}%`}));
      const categorySheet = bundle.categoryDist.map(r=>({Category:r.label, Count:r.value, "%":`${r.pct}%`}));
      const slaSheet = bundle.slaSummary.map(r=>({SLA:r.label, Count:r.value, "%":`${r.pct}%`}));
      excelDownload([
        {name:"Summary", rows:summarySheet},
        {name:"KPI Summary", rows:kpiSheet},
        {name:"Team Performance", rows:teamSheet},
        {name:"Ticket Details", rows:taskSheet},
        {name:"Status Distribution", rows:statusSheet},
        {name:"Priority Distribution", rows:prioritySheet},
        {name:"Category Analysis", rows:categorySheet},
        {name:"SLA Summary", rows:slaSheet},
      ], `manager_report_${Date.now()}.xlsx`);
    } finally { setReportGenerating(null); }
  };

  const downloadReportPdf = async (scope="global") => {
    setReportGenerating("pdf");
    try {
      // Give any chart still mid-resize (or a dashboard tab just switched
      // into/out of view) a moment to settle before reading canvases below.
      await waitForChartsReady();
      const bundle = buildReportBundle(scope);
      const doc = new jsPDF();
      const green = [21,128,61];

      doc.setFontSize(16); doc.setTextColor(...green); doc.setFont(undefined,"bold");
      doc.text("Manager Ticket Performance Report", 14, 18);
      doc.setFontSize(9.5); doc.setTextColor(90,90,90); doc.setFont(undefined,"normal");
      doc.text(`Manager: ${bundle.meta.managerName}   |   Generated: ${bundle.meta.generatedAt.toLocaleString()}`, 14, 25);
      doc.text(`Applied Filters: ${bundle.meta.filters.join(" • ")}`, 14, 30);
      doc.text(`Total Records: ${bundle.meta.totalRecords} Tickets`, 14, 35);

      autoTable(doc, {
        startY: 40, theme:"grid", headStyles:{fillColor:green},
        head:[["KPI Summary","Value"]], body:bundle.kpi.map(k=>[k.label, String(k.value)]),
        margin:{left:14, right:14}, tableWidth: 90,
      });

      let y = doc.lastAutoTable.finalY + 8;
      autoTable(doc, {
        startY:y, theme:"grid", headStyles:{fillColor:green},
        head:[["User","Role","Assigned","Completed","Pending","Overdue","Completion %"]],
        body:bundle.team.map(m=>[m.name,m.role,m.assigned,m.completed,m.pending,m.overdue,m.completionRate]),
        margin:{left:14, right:14},
      });

      // Side-by-side table pairs: autoTable's own doc.lastAutoTable only
      // ever reflects the table from the MOST RECENT call, so after the
      // 2nd (right) call it silently drops however far down the 1st (left)
      // table actually ran -- if the left table had more rows, the next
      // block would start too high and print on top of it. Track both
      // finalY values explicitly and continue from whichever is taller.
      y = doc.lastAutoTable.finalY + 8;
      autoTable(doc, {
        startY:y, theme:"grid", headStyles:{fillColor:green},
        head:[["Status Distribution","Count","%"]], body:bundle.statusDist.map(r=>[r.label,r.value,`${r.pct}%`]),
        margin:{left:14, right:14}, tableWidth: 90,
      });
      const statusFinalY = doc.lastAutoTable.finalY;
      autoTable(doc, {
        startY:y, theme:"grid", headStyles:{fillColor:green},
        head:[["Priority Distribution","Count","%"]], body:bundle.priorityDist.map(r=>[r.label,r.value,`${r.pct}%`]),
        margin:{left:110, right:14}, tableWidth: 86,
      });
      y = Math.max(statusFinalY, doc.lastAutoTable.finalY) + 8;

      autoTable(doc, {
        startY:y, theme:"grid", headStyles:{fillColor:green},
        head:[["Category Analysis","Count","%"]], body:bundle.categoryDist.map(r=>[r.label,r.value,`${r.pct}%`]),
        margin:{left:14, right:14}, tableWidth: 90,
      });
      const categoryFinalY = doc.lastAutoTable.finalY;
      autoTable(doc, {
        startY:y, theme:"grid", headStyles:{fillColor:green},
        head:[["SLA Summary","Count","%"]], body:bundle.slaSummary.map(r=>[r.label,r.value,`${r.pct}%`]),
        margin:{left:110, right:14}, tableWidth: 86,
      });
      y = Math.max(categoryFinalY, doc.lastAutoTable.finalY) + 8;

      // Graph snapshots (§7) -- same three charts already rendered on the
      // dashboard (Ticket Status donut, User Performance bar, Ticket Trend
      // bar); a chart whose section is currently empty (no ref, e.g. no
      // trend data) is simply skipped rather than left as a broken image.
      // composeChartSnapshot() reads each chart's already-rendered canvas
      // as-is and, where a legend adds real information, draws simple
      // color-swatch + text rows below it on a plain offscreen canvas
      // (built from the same label/count/% data already on screen) --
      // never touching the live chart's own Chart.js options, so the chart
      // itself can't be corrupted or affected. Each image keeps its real
      // aspect ratio inside a generous bounding box, instead of being
      // stretched to one fixed size for every chart regardless of shape.
      // Ticket Status Distribution is no longer captured as a chart image here
      // -- the redesign consolidated status counts down to ONE on-page
      // visualization (the segmented-bar Ticket Status Overview section,
      // which isn't a chart.js canvas), and the same numbers are already
      // tabled in full via bundle.statusDist (autoTable) earlier in this
      // PDF, so nothing is actually lost by dropping the duplicate image.
      // Every entry below is always kept (never filtered out) -- a chart
      // with no snapshot (empty data, filtered user with no activity, or a
      // canvas that wasn't rendered/visible yet) renders as a "No chart
      // data available" placeholder box instead, so PDF export never just
      // silently loses a section or crashes on it.
      const CHART_MAX_W = 172, CHART_MAX_H = 110;
      const trendLegend = trendChartData.datasets.map(ds=>({text:ds.label, color:ds.backgroundColor}));
      const chartSnapshots = [
        {title:"User Performance (Completed Tickets)", cap:composeChartSnapshot(userPerfChartRef.current)},
        {title:"Ticket Trend (Created / Completed / Pending)", cap:composeChartSnapshot(trendChartRef.current, trendLegend)},
      ].map(c=>{
        const cw = c.cap?.w, ch = c.cap?.h;
        const ratio = (cw && ch) ? cw/ch : 16/9;
        let w = CHART_MAX_W, h = w/ratio;
        if(h > CHART_MAX_H){ h = CHART_MAX_H; w = h*ratio; }
        return {title:c.title, img:c.cap?.img||null, w, h};
      });

      if(chartSnapshots.length){
        doc.addPage();
        doc.setFontSize(13); doc.setTextColor(...green); doc.setFont(undefined,"bold");
        doc.text("Graph Snapshots", 14, 18);
        let gy = 28;
        chartSnapshots.forEach(({title,img,w,h})=>{
          if(gy + h + 16 > 280){ doc.addPage(); gy=20; }
          doc.setFontSize(10.5); doc.setTextColor(60,60,60); doc.setFont(undefined,"bold");
          doc.text(title, 14, gy);
          if (img) {
            doc.addImage(img, "PNG", 14, gy+4, w, h);
          } else {
            doc.setDrawColor(224,224,220); doc.setFillColor(248,247,245);
            doc.rect(14, gy+4, w, h, "FD");
            doc.setFontSize(9); doc.setTextColor(150,148,145); doc.setFont(undefined,"italic");
            doc.text("No chart data available", 14+w/2, gy+4+h/2, {align:"center"});
            doc.setFont(undefined,"bold");
          }
          gy += h + 20;
        });
      }

      doc.addPage();
      autoTable(doc, {
        startY:20, theme:"striped", headStyles:{fillColor:green}, styles:{fontSize:7.5, cellPadding:2},
        head:[["ID","Ticket","Category","Priority","Status","Assigned","Created","Due","SLA"]],
        body:bundle.tasks.map(t=>[t.id,t.title,t.category,t.priority,t.status,t.assignedUser,t.createdDate,t.dueDate,t.slaStatus]),
        margin:{left:14, right:14},
      });

      doc.save(`manager_report_${Date.now()}.pdf`);
    } finally { setReportGenerating(null); }
  };

  // NEW: User Performance Preview popup's own PDF download (§9) — kept
  // separate from downloadReportPdf above rather than branching it:
  // downloadReportPdf is also called from the header's "Export Dashboard"
  // button and the Generate Report modal, both of which intentionally
  // embed the team-wide ranking chart (userPerfChartRef) as context.
  // Embedding that same team-wide chart into what's supposed to be ONE
  // person's report would leak every other member's numbers into their
  // file. This function only ever reads selectedMemberStats/
  // previewFilteredTasks/previewStats/previewStatusRows/previewTatBreakdown
  // — the popup's own time-filtered (previewPeriod-scoped) values, same
  // ones the popup itself renders — plus its three chart refs
  // (userPreviewStatusChartRef/SlaChartRef/TrendChartRef). No new fetch, no
  // new calculation. So the downloaded report always matches whatever
  // period the popup's own filter is currently showing, not the page-wide
  // gPeriod behind it.
  const downloadUserPerformancePdf = async () => {
    if(!popupMemberStats) return;
    setReportGenerating("pdf");
    try {
      // Same as downloadReportPdf: let any in-flight chart resize settle
      // before this popup's own three charts are captured below.
      await waitForChartsReady();
      const name = popupMemberStats.name;
      const safeName = name.trim().replace(/\s+/g,"_").replace(/[^\w-]/g,"") || "User";
      const periodLabel = previewPeriodLabel;
      const doc = new jsPDF();
      const green = [21,128,61];

      doc.setFontSize(16); doc.setTextColor(...green); doc.setFont(undefined,"bold");
      doc.text("User Performance Report", 14, 18);
      doc.setFontSize(9.5); doc.setTextColor(90,90,90); doc.setFont(undefined,"normal");
      doc.text(`User: ${name}   |   Role: ${popupMemberStats.role}`, 14, 25);
      doc.text(`Reporting Period: ${periodLabel}   |   Generated: ${dashNow.toLocaleString()}`, 14, 30);

      autoTable(doc, {
        startY: 38, theme:"grid", headStyles:{fillColor:green},
        head:[["Performance Summary","Value"]],
        body:[
          ["Total Tickets", previewStats.assigned],
          ["Completed Tickets", previewStats.completed],
          ["Pending Tickets", previewStats.pending],
          ["In Progress", previewStats.inProgress],
          ["Overdue", previewStats.overdue],
          ["Completion Rate", previewStats.completionRate===null?"—":`${previewStats.completionRate}%`],
          ["SLA Compliance", previewStats.slaPct===null?"—":`${previewStats.slaPct}%`],
        ],
        margin:{left:14, right:14}, tableWidth: 100,
      });

      // Graph snapshots -- this popup's own three charts, captured exactly
      // as they render on screen (same composeChartSnapshot() helper the
      // shared Generate Report PDF already uses above). Every entry is
      // always kept -- a chart the selected user has no data for (or one
      // that wasn't visible/rendered yet) renders as a "No chart data
      // available" placeholder instead of vanishing or crashing capture.
      const CHART_MAX_W = 172, CHART_MAX_H = 100;
      const statusLegend = previewStatusRows.map(r=>({text:`${r.label}: ${r.val} (${r.pct}%)`, color:r.color}));
      const chartSnapshots = [
        {title:"Ticket Status", cap:composeChartSnapshot(userPreviewStatusChartRef.current, statusLegend)},
        {title:"SLA Performance", cap:composeChartSnapshot(userPreviewSlaChartRef.current)},
        {title:"Productivity Trend", cap:composeChartSnapshot(userPreviewTrendChartRef.current)},
      ].map(c=>{
        const cw = c.cap?.w, ch = c.cap?.h;
        const ratio = (cw && ch) ? cw/ch : 16/9;
        let w = CHART_MAX_W, h = w/ratio;
        if(h > CHART_MAX_H){ h = CHART_MAX_H; w = h*ratio; }
        return {title:c.title, img:c.cap?.img||null, w, h};
      });

      let y = doc.lastAutoTable.finalY + 10;
      if(chartSnapshots.length){
        doc.setFontSize(12); doc.setTextColor(...green); doc.setFont(undefined,"bold");
        doc.text("Graphs", 14, y); y += 6;
        chartSnapshots.forEach(({title,img,w,h})=>{
          if(y + h + 14 > 280){ doc.addPage(); y=20; }
          doc.setFontSize(10); doc.setTextColor(60,60,60); doc.setFont(undefined,"bold");
          doc.text(title, 14, y);
          if (img) {
            doc.addImage(img, "PNG", 14, y+3, w, h);
          } else {
            doc.setDrawColor(224,224,220); doc.setFillColor(248,247,245);
            doc.rect(14, y+3, w, h, "FD");
            doc.setFontSize(9); doc.setTextColor(150,148,145); doc.setFont(undefined,"italic");
            doc.text("No chart data available", 14+w/2, y+3+h/2, {align:"center"});
            doc.setFont(undefined,"bold");
          }
          y += h + 16;
        });
      }

      doc.addPage();
      doc.setFontSize(12); doc.setTextColor(...green); doc.setFont(undefined,"bold");
      doc.text(`Ticket List — ${name}`, 14, 18);
      autoTable(doc, {
        startY: 24, theme:"striped", headStyles:{fillColor:green}, styles:{fontSize:8, cellPadding:2.2},
        head:[["Ticket Name","Priority","Status","Due / SLA","Completion %","Category"]],
        body:previewFilteredTasks.map(task=>{
          const priorityInfo = getPriorityInfo(task.priority);
          return [
            getText(taskDisplayTitle(task),""),
            priorityInfo?priorityInfo.label:"Unspecified",
            STATUS_LABELS[normalizeStatus(task.status)]||titleCase(normalizeStatus(task.status)),
            getDueBadge(task,dashNow).label,
            `${getTaskProgress(task)}%`,
            getText(task.category_name,"")||"Uncategorized",
          ];
        }),
        margin:{left:14, right:14},
      });

      doc.save(`${safeName}_Performance_Report.pdf`);
    } finally { setReportGenerating(null); }
  };

  // NEW: User Performance Preview popup's own Excel download -- mirrors
  // downloadUserPerformancePdf above (same popupMemberStats/previewStats/
  // previewStatusRows/previewTatBreakdown/previewFilteredTasks, the popup's
  // own previewPeriod-scoped values), just as a workbook via the same
  // excelDownload helper downloadReportExcel already uses, instead of a PDF.
  const downloadUserPerformanceExcel = () => {
    if(!popupMemberStats) return;
    setReportGenerating("excel");
    try {
      const name = popupMemberStats.name;
      const safeName = name.trim().replace(/\s+/g,"_").replace(/[^\w-]/g,"") || "User";
      const summarySheet = [
        {Field:"Report", Value:"User Performance Report"},
        {Field:"User", Value:name},
        {Field:"Role", Value:popupMemberStats.role},
        {Field:"Reporting Period", Value:previewPeriodLabel},
        {Field:"Generated", Value:dashNow.toLocaleString()},
      ];
      const performanceSheet = [
        {Metric:"Total Tickets", Value:previewStats.assigned},
        {Metric:"Completed Tickets", Value:previewStats.completed},
        {Metric:"Pending Tickets", Value:previewStats.pending},
        {Metric:"In Progress", Value:previewStats.inProgress},
        {Metric:"Overdue", Value:previewStats.overdue},
        {Metric:"Completion Rate", Value:previewStats.completionRate===null?"—":`${previewStats.completionRate}%`},
        {Metric:"SLA Compliance", Value:previewStats.slaPct===null?"—":`${previewStats.slaPct}%`},
      ];
      const statusSheet = previewStatusRows.map(r=>({Status:r.label, Count:r.val, "%":`${r.pct}%`}));
      const slaSheet = [
        {SLA:"Within SLA", Count:previewTatBreakdown.withinTat, "%":`${previewTatBreakdown.withinTatPct}%`},
        {SLA:"Near Risk", Count:previewTatBreakdown.atRisk, "%":`${previewTatBreakdown.atRiskPct}%`},
        {SLA:"Overdue", Count:previewTatBreakdown.overdue, "%":`${previewTatBreakdown.overduePct}%`},
      ];
      const taskSheet = previewFilteredTasks.map(task=>{
        const priorityInfo = getPriorityInfo(task.priority);
        return {
          "Ticket Name":getText(taskDisplayTitle(task),""),
          Priority:priorityInfo?priorityInfo.label:"Unspecified",
          Status:STATUS_LABELS[normalizeStatus(task.status)]||titleCase(normalizeStatus(task.status)),
          "Due / SLA":getDueBadge(task,dashNow).label,
          "Completion %":getTaskProgress(task),
          Category:getText(task.category_name,"")||"Uncategorized",
        };
      });
      excelDownload([
        {name:"Summary", rows:summarySheet},
        {name:"Performance Summary", rows:performanceSheet},
        {name:"Status Distribution", rows:statusSheet},
        {name:"SLA Summary", rows:slaSheet},
        {name:"Ticket List", rows:taskSheet},
      ], `${safeName}_Performance_Report.xlsx`);
    } finally { setReportGenerating(null); }
  };

  /* ════ RENDER ════ */
  return (
    <div className="md-page">

      {/* ── Header ──────────────────────────────── */}
      <div className="md-header">
        <div className="md-header-profile">
          <div className="md-header-avatar" title={managerName}>{managerName.charAt(0).toUpperCase()}</div>
        <div className="md-header-id">
          <div className="md-breadcrumb">Manager Panel</div>
          <h1 className="md-title">{greetingWord()}, <span>{managerName}</span></h1>
          <div className="md-header-meta">
            <span className="md-header-role">{managerRole}</span>
            <span className="md-header-dot">•</span>
            <span className="md-hero-date">{todayLabel}</span>
            {lastUpdatedAt && <><span className="md-header-dot">•</span><span className="md-updated-label">Updated {timeAgo(lastUpdatedAt)}</span></>}
          </div>
          <p className="md-subtitle">Monitor team workload, assignments, SLA performance and ticket progress.</p>
        </div>
        </div>
        <div className="md-header-actions">
          <button className="md-btn md-btn-ghost" onClick={fetchDashboardData} disabled={loading}>
            {loading ? <><Spin/> Refreshing…</> : <>
              <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path d="M1 4v6h6M23 20v-6h-6"/><path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4-4.64 4.36A9 9 0 0 1 3.51 15"/>
              </svg>Refresh</>}
          </button>
          <button className="md-btn md-btn-ghost" onClick={()=>downloadReportExcel()} disabled={loading || !!reportGenerating || dashboardStats.total===0} title="One-click Excel export of the current view">
            {reportGenerating==="excel" ? <><Spin/> Exporting…</> : <><FaFileExcel/> Export Dashboard</>}
          </button>
          <button className="md-btn md-btn-primary" onClick={openReportModal} disabled={loading || dashboardStats.total===0}>
            <FaFileExport/> Generate Report
          </button>
          {(!can || can("tasks","create")) && (
            <button className="md-btn md-btn-primary" onClick={()=>{resetCreate();setShowCreate(true);}}>
              <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14"/></svg>
              New Ticket
            </button>
          )}
        </div>
      </div>

      {/* ── NEW: Top-level tab navigation ─────────────────────────
           Purely a visibility switch over sections that already exist
           below (still all mounted, just shown/hidden via activeTab) --
           no section's state, handlers or data flow changes because of
           which tab is active. ── */}
      <div className="md-maintabs" role="tablist" aria-label="Manager dashboard sections">
        {[
          {key:"overview",    label:"Overview Dashboard",     Icon:FaChartPie},
          {key:"team",        label:"Team Management",        Icon:FaUserGroup},
          {key:"tasks",       label:"Ticket Management",         Icon:FaListCheck},
          {key:"performance", label:"Performance Analytics",   Icon:FaChartLine},
          {key:"reports",     label:"Reports & Insights",      Icon:FaFileExport},
        ].map(t=>{
          const TabIcon = t.Icon;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={activeTab===t.key}
              className={`md-maintab${activeTab===t.key?" active":""}`}
              onClick={()=>setActiveTab(t.key)}
            >
              <TabIcon className="md-maintab-icon" aria-hidden="true"/>
              <span>{t.label}</span>
            </button>
          );
        })}
      </div>

      {/* ── NEW: Global dashboard filters — moved outside the per-tab
           panels so it's visible and editable on every tab (Overview,
           Team Management, Ticket Management, Performance Analytics,
           Reports & Insights), since gPeriod/gMember/gRole/gStatus/
           gPriority/gCategory already drive data on all of them via
           globalFilteredTasks/memberStatsList, not just Overview. ── */}
      <div className="md-card md-filterbar">
        <div className="md-filterbar-row">
          <div className="md-pill-group">
            {[["all","All Time"],["today","Today"],["week","This Week"],["month","This Month"],["custom","Custom"]].map(([k,l])=>(
              <button key={k} type="button" className={`md-pill-btn${gPeriod===k?" active":""}`} onClick={()=>setGPeriod(k)}>{l}</button>
            ))}
          </div>
          {gPeriod==="custom" && (
            <div className="md-filter-daterow">
              <input type="date" className="md-select" value={gCustomFrom} onChange={e=>setGCustomFrom(e.target.value)} />
              <span className="md-filter-to">to</span>
              <input type="date" className="md-select" value={gCustomTo} onChange={e=>setGCustomTo(e.target.value)} />
            </div>
          )}
        </div>
        <div className="md-filterbar-row">
          <select className="md-select" value={gMember} onChange={e=>setGMember(e.target.value)}>
            <option value="ALL">All Members</option>
            {myTeamMembers.map(m=><option key={getIdValue(m)||getText(m)} value={getIdValue(m)}>{getText(m)}</option>)}
          </select>
          <select className="md-select" value={gRole} onChange={e=>setGRole(e.target.value)}>
            <option value="ALL">All Roles</option>
            {roleOptions.map(r=><option key={r} value={r}>{r}</option>)}
          </select>
          <select className="md-select" value={gStatus} onChange={e=>setGStatus(e.target.value)}>
            <option value="ALL">All Status</option>
            {statusRows.map(s=><option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          {priorityOptions.length>0 && (
            <select className="md-select" value={gPriority} onChange={e=>setGPriority(e.target.value)}>
              <option value="ALL">All Priority</option>
              {priorityOptions.map(p=><option key={p} value={p}>{titleCase(p)}</option>)}
            </select>
          )}
          {categories.length>0 && (
            <select className="md-select" value={gCategory} onChange={e=>setGCategory(e.target.value)}>
              <option value="ALL">All Categories</option>
              {categories.map(c=><option key={getIdValue(c)||getText(c)} value={String(getIdValue(c))}>{getText(c)}</option>)}
            </select>
          )}
          {hasActiveGlobalFilters && <button className="md-btn md-btn-ghost md-btn-sm" onClick={resetGlobalFilters}>Reset Filters</button>}
        </div>
        <div className="md-viewing-row">
          <span className="md-viewing-label"><strong>Viewing:</strong> {viewingLabel}</span>
          {hasActiveGlobalFilters && <button className="md-viewing-clear" onClick={resetGlobalFilters}>Clear Filters</button>}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════
          TAB: Overview Dashboard (default) -- executive summary:
          Filters, KPI Cards, Needs Your Attention, Quick Actions.
      ══════════════════════════════════════════════════════════ */}
      <div className={`md-tabpanel${activeTab==="overview"?" md-tabpanel-active":""}`} style={{display:activeTab==="overview"?undefined:"none"}} role="tabpanel">

      {/* ── KPI summary (§3) — 6 cards, spec order: Total, Pending, In
           Progress, Overdue, Completed, Team. "Active/Open" and "Avg
           Progress" (redundant with Pending+In Progress and with the
           Completion Rate tile in the SLA Health card below) were dropped
           to match the spec's exact 6-card recommendation. ── */}
      <div className="md-kpi-grid ui-animate-stagger">
        {[
          {cls:"total",     Icon:FaListCheck,          val:dashboardStats.total,        lbl:"Total Tickets",     meta:"In current view",
            go:openComplaints()},
          {cls:"pending",   Icon:FaClock,              val:dashboardStats.pending,      lbl:"Pending",         meta:"Waiting to start",
            go:openComplaints("pending")},
          {cls:"inprogress",Icon:FaSpinner,            val:dashboardStats.inProgress,   lbl:"In Progress",     meta:"Actively worked on",
            go:openComplaints("in_progress")},
          {cls:"overdue",   Icon:FaTriangleExclamation,val:dashboardStats.overdue,      lbl:"Overdue",         meta:dashboardStats.overdue>0?"Needs attention":"Past due date",
            go:openComplaints()},
          {cls:"completed", Icon:FaCircleCheck,        val:dashboardStats.completed,    lbl:"Completed",       meta:"Done / closed",
            go:openComplaints("completed")},
          {cls:"members",   Icon:FaUserGroup,          val:myTeamMembers.length,        lbl:"Team Members",   meta:"Associated with you",
            go:scrollToTeam},
        ].map(s=>{
          const StatIcon = s.Icon;
          return (
          <div key={s.cls} className={`md-stat ${s.cls}`} role="button" tabIndex={0}
            onClick={s.go}
            onKeyDown={e=>{ if(e.key==="Enter"||e.key===" "){ e.preventDefault(); s.go(); } }}>
            <div className="md-stat-icon"><StatIcon/></div>
            <div>
              <div className="md-stat-val">{s.val}</div>
              <div className="md-stat-lbl">{s.lbl}</div>
              <div className="md-stat-meta">{s.meta}</div>
            </div>
          </div>
          );
        })}
      </div>

      {/* Dynamic Forms visibility audit, §9: team/project-scoped form
          responses route to the same Forms Workspace -> Review & Reports ->
          Responses tab every role reaches, whose SELF/TEAM/PROJECT/ALL
          scoping is resolved server-side (controllers/reportController.js's
          resolveReportScope, via the manager's OWN resolved
          reports:view_form_results grant) -- never re-filtered on the
          frontend. The link itself is gated only on the coarse permission
          (same posture as every other Forms Workspace tab's visibility
          check); a manager without a broader-than-SELF grant still sees
          only their own responses there, correctly, not an empty/broken
          page. */}
      {can && can("reports", "view_form_results") && (
        <div className="md-card md-page-section" style={{ padding: "14px 18px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <strong>Team Forms</strong>
            <p className="text-muted small mb-0">Form responses across your team/projects, scoped to what you're permitted to see.</p>
          </div>
          <button type="button" className="md-btn md-btn-primary" onClick={() => navigate("/manager/forms?tab=review&view=responses&only=team-responses")}>
            View Team Forms
          </button>
        </div>
      )}

      {/* ── Unified empty state when no records match the current filters
           — moved up here (from lower on the page) since at zero records
           every section below is moot; showing it right under the KPIs
           makes that immediately clear instead of after scrolling past
           several empty sections. ── */}
      {dashboardStats.total===0 && (
        <div className="md-card md-report-empty md-page-section">
          <div className="md-report-empty-icon"><FaFolderOpen/></div>
          <h5>No data available for selected filters</h5>
          <p>Try widening the date range or clearing a filter to see matching tickets.</p>
          {hasActiveGlobalFilters && <button className="md-btn md-btn-primary md-btn-sm" onClick={resetGlobalFilters}>Clear Filters</button>}
        </div>
      )}

      {/* ── Needs Your Attention (§4) — moved directly under the KPIs,
           ahead of Top Performers, per the redesign spec: actionable
           issues outrank performance reporting for a manager's first
           screen. Content/logic unchanged. ── */}
      <div className="md-card md-section-card md-page-section">
        <div className="md-section-head">
          <h5>Needs Your Attention</h5>
          <p>Actionable issues across your team, right now.</p>
        </div>

        {outsideTeamStats.active>0 && (
          <div className="md-critical-alert" onClick={openComplaints()} role="button">
            <div className="md-critical-head">
              <span className="md-critical-badge">Critical Assignment Exception</span>
            </div>
            <div className="md-critical-title">{outsideTeamStats.active} active ticket{outsideTeamStats.active===1?"":"s"} {outsideTeamStats.active===1?"is":"are"} outside the tracked team</div>
            <div className="md-critical-breakdown">
              <span><strong>{outsideTeamStats.pending}</strong> Pending</span>
              <span><strong>{outsideTeamStats.inProgress}</strong> In Progress</span>
              <span className={outsideTeamStats.overdue>0?"md-text-danger":""}><strong>{outsideTeamStats.overdue}</strong> Overdue</span>
            </div>
            {teamExceptions.membersWithNoActive>0 && (
              <div className="md-critical-sub">{teamExceptions.membersWithNoActive} of {myTeamMembers.length} team member{myTeamMembers.length===1?"":"s"} currently {teamExceptions.membersWithNoActive===1?"has":"have"} no active work</div>
            )}
            <span className="md-critical-cta">Review Tickets →</span>
          </div>
        )}

        <div className="md-attention-list">
          {attentionItems.length ? attentionItems.map(item=>(
            <button key={item.key} type="button" className={`md-attention-row md-attn-${item.tone}`} onClick={item.action}>
              <span className="md-attn-icon"><item.Icon/></span>
              <span className="md-attn-body">
                <span className="md-attn-title">{item.title}</span>
                <span className="md-attn-sub">{item.sub}</span>
              </span>
              <span className="md-attn-cta">{item.cta}</span>
            </button>
          )) : (
            <div className="md-attention-row md-attn-success" style={{cursor:"default"}}>
              <span className="md-attn-icon"><FaCircleCheck/></span>
              <span className="md-attn-body"><span className="md-attn-title">Nothing needs your attention right now.</span></span>
            </div>
          )}
        </div>
      </div>

      {/* ── Quick Actions (§15) — compact shortcuts reusing only existing
           handlers/routes/scroll targets already wired elsewhere on this
           page (New Ticket modal, Generate Report modal, My Team's own
           scrollToTeam ref, etc.); no new backend logic. Moved up here
           (was previously much further down the page) so it sits with the
           rest of the Overview tab's high-level, at-a-glance content.
           "View Products" is gated on the same products:view permission
           the actual /manager/products route already enforces
           (PermissionRoute in AppRoutes.js) -- Approvals/Complaints have no
           equivalent granular permission in this app (they're manager-role
           pages, not permission-gated routes), so they're left as-is. */}
      <div className="md-card md-section-card md-actions-card">
        <div className="md-section-head"><h5>Quick Actions</h5></div>
        <div className="md-actions-row">
          {(!can || can("tasks","create")) && (
            <button className="md-action-chip md-action-chip-primary" onClick={()=>{resetCreate();setShowCreate(true);}}>+ Create Ticket</button>
          )}
          {dashboardStats.overdue>0 && (
            <button className="md-action-chip" onClick={scrollToTaskTable}>Review {dashboardStats.overdue} Overdue</button>
          )}
          <button className="md-action-chip" onClick={openReportModal}>Generate Report</button>
          <button className="md-action-chip" onClick={scrollToTeam}>Manage Team</button>
          {withoutCategoryCount>0 && (
            <button className="md-action-chip" onClick={scrollToTaskTable}>Review {withoutCategoryCount} Missing Category</button>
          )}
          {pendingApprovalsCount>0 && (
            <button className="md-action-chip" onClick={()=>navigate("/manager/approvals")}>Review Staff Approvals</button>
          )}
          {complaintsSummary.open>0 && (
            <button className="md-action-chip" onClick={()=>navigate("/manager/complaints")}>Open Complaints</button>
          )}
          {(!can || can("products","view")) && (
            <button className="md-action-chip" onClick={()=>navigate("/manager/products")}>View Products</button>
          )}
        </div>
      </div>

      </div>{/* /md-tabpanel: overview */}

      {/* ══════════════════════════════════════════════════════════
          TAB: Team Management -- workload, availability, capacity.
      ══════════════════════════════════════════════════════════ */}
      <div className={`md-tabpanel${activeTab==="team"?" md-tabpanel-active":""}`} style={{display:activeTab==="team"?undefined:"none"}} role="tabpanel">

      {/* ── Team Workload / Capacity (formerly paired side-by-side with
           Top Performers; Top Performers/Ranking now lives in the
           Performance Analytics tab instead). The old standalone "Team
           Exceptions" card (people-count tiles) and the old standalone
           "Team Workload Balance" section have both been folded into this
           panel (capacityRows, computed above) instead of showing
           per-member workload in three separate places. ── */}
      <div className="md-card md-section-card md-page-section">
          <div className="md-section-head">
            <h5><FaChartSimple className="md-anacard-icon"/> Team Workload / Capacity</h5>
            <p>Current active load per team member.</p>
          </div>
          {capacityRows.length===0 ? (
            <div className="md-empty">
              <div className="md-empty-icon"><FaBriefcase/></div>
              <h5>No workload data yet</h5>
              <p>Capacity will appear once tickets are assigned to your team.</p>
            </div>
          ) : (
            <>
              <div className="md-capacity-list">
                {capacityVisible.map(m=>(
                  <div key={m.id} className="md-capacity-row">
                    <div className="md-capacity-top">
                      <span className="md-capacity-name" title={m.name}>{m.name}</span>
                      <span className={`md-capacity-tag md-capacity-tag-${m.capacity}`}>{CAPACITY_LABELS[m.capacity]}</span>
                    </div>
                    <div className="md-capacity-track">
                      <div className={`md-capacity-fill md-capacity-fill-${m.capacity}`} style={{width:`${Math.round(m.active/capacityMax*100)}%`}}/>
                    </div>
                    <div className="md-capacity-stats">
                      <span className="md-capacity-stat">Assigned <strong>{m.assigned}</strong></span>
                      <span className="md-capacity-stat">In Progress <strong>{m.inProgress}</strong></span>
                      <span className="md-capacity-stat">Pending <strong>{m.pending}</strong></span>
                      <span className="md-capacity-stat">Completed <strong>{m.completed}</strong></span>
                      <span className={`md-capacity-stat${m.overdue>0?" danger":""}`}>Overdue <strong>{m.overdue}</strong></span>
                    </div>
                  </div>
                ))}
              </div>
              {capacityRows.length>6 && (
                <div className="md-myteam-viewall">
                  <button className="md-btn md-btn-ghost md-btn-sm" onClick={()=>setCapacityShowAll(v=>!v)}>
                    {capacityShowAll ? "Show Less" : `View All (${capacityRows.length})`}
                  </button>
                </div>
              )}
            </>
          )}
      </div>

      {/* Top Performers / Team Performance Ranking now lives in the
           Performance Analytics tab below. */}
      {/* ── NEW: My Team ──────────────────────────────────────────── */}
      <div className="md-card md-section-card md-myteam-section" ref={teamRef}>
        <div className="md-section-head md-team-head md-myteam-head">
          <div>
            <h5>My Team</h5>
            <p>Quick view of who's working on what right now.</p>
          </div>
          <div className="md-team-toolbar md-myteam-toolbar">
            <div className="md-search-wrap md-myteam-searchwrap">
              <span className="md-search-icon">
                <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
              </span>
              <input className="md-search md-myteam-search" placeholder="Search member…" value={teamSearch} onChange={e=>setTeamSearch(e.target.value)} />
            </div>
          </div>
        </div>

        <div className="md-myteam-capacity">
          <span className="md-myteam-badge md-myteam-badge-neutral"><FaUserGroup/> <strong>{myTeamMembers.length}</strong> Members</span>
          <span className="md-myteam-badge md-myteam-badge-available"><FaUserCheck/> <strong>{teamExceptions.membersWithNoActive}</strong> Available</span>
          <span className="md-myteam-badge md-myteam-badge-active"><FaUserClock/> <strong>{teamExceptions.membersWithActive}</strong> Active</span>
          {outsideTeamStats.active>0 && (
            <span className="md-myteam-badge md-myteam-badge-warn"><FaTriangleExclamation/> <strong>{outsideTeamStats.active}</strong> Ticket{outsideTeamStats.active===1?"":"s"} Need Review</span>
          )}
        </div>

        {teamRoster.length===0 ? (
          <div className="md-empty">
            <div className="md-empty-icon"><FaUserGroup/></div>
            <h5>No team members found</h5>
            <p>No employees are currently associated with your account, or none match this filter.</p>
          </div>
        ) : (
          <>
            <div className="md-myteam-grid">
              {teamVisible.map(m=>{
                const status = ownerStatusInfo(m);
                const meta = MYTEAM_STATUS_META[status.level]||MYTEAM_STATUS_META.none;
                const StatusIcon = meta.icon;
                const isSelected = showUserPreview && previewMemberId===m.id;
                const perf = performanceStatusFor(m);
                return (
                <div key={m.id||m.name} className={`md-myteam-card md-myteam-card-${meta.tone}${isSelected?" md-myteam-card-selected":""}`}>
                  <div className="md-myteam-card-top">
                    <div className="md-myteam-avatar">{m.name.charAt(0).toUpperCase()}</div>
                    <div className="md-myteam-id">
                      <div className="md-myteam-name" title={m.name}>{m.name}</div>
                      <span className="md-myteam-role"><FaUserTag/> {m.role}</span>
                    </div>
                  </div>
                  <div className="md-myteam-badge-row">
                    <span className={`md-mt-badge md-mt-badge-${status.level}`}><StatusIcon/> {status.label}</span>
                    {perf && <span className={`md-perftag md-perftag-${perf.cls}`}>{perf.label}</span>}
                  </div>

                  {m.assigned===0 ? (
                    <div className="md-myteam-empty">
                      <span className="md-myteam-empty-icon"><FaCircleCheck/></span>
                      <span className="md-myteam-empty-title">Available</span>
                      <span className="md-myteam-empty-sub">Ready for new assignments</span>
                    </div>
                  ) : (
                    <>
                      <div className="md-myteam-active">
                        <span className="md-myteam-active-lbl"><FaListCheck/> Active Tickets</span>
                        <span className="md-myteam-active-val">{m.active}</span>
                      </div>
                      <div className="md-myteam-mini-grid">
                        <div className="md-myteam-mini">
                          <span className="md-myteam-mini-lbl"><FaClock/> Pending</span>
                          <strong className="md-myteam-mini-val mt-amber">{m.pending}</strong>
                        </div>
                        <div className="md-myteam-mini">
                          <span className="md-myteam-mini-lbl"><FaSpinner/> In Progress</span>
                          <strong className="md-myteam-mini-val mt-blue">{m.inProgress}</strong>
                        </div>
                        <div className="md-myteam-mini">
                          <span className="md-myteam-mini-lbl"><FaTriangleExclamation/> Overdue</span>
                          <strong className="md-myteam-mini-val mt-red">{m.overdue}</strong>
                        </div>
                      </div>
                      <div className="md-myteam-footer">
                        <span><FaCheckDouble/> Completed <strong>{m.completed}</strong></span>
                        <span>Completion <strong className="mt-green">{m.completionRate===null?"—":`${m.completionRate}%`}</strong></span>
                      </div>
                      {m.completionRate!==null && (
                        <div className="md-myteam-progress-track">
                          <div className="md-myteam-progress-fill" style={{width:`${m.completionRate}%`}}/>
                        </div>
                      )}
                    </>
                  )}
                  <button type="button" className="md-myteam-view-btn" onClick={()=>isSelected?setShowUserPreview(false):previewUserDetails(m.id)}>
                    <FaEye/> {isSelected ? "Viewing ✓" : "View Details"}
                  </button>
                </div>
                );
              })}
            </div>
            {teamRoster.length>8 && (
              <div className="md-myteam-viewall">
                <button className="md-btn md-btn-ghost md-btn-sm" onClick={()=>setTeamShowAll(v=>!v)}>
                  {teamShowAll ? "Show Less" : `View All (${teamRoster.length})`}
                </button>
              </div>
            )}
          </>
        )}
        {(outsideTeamStats.assigned>0 || noOwnerStats.assigned>0) && (
          <div className="md-myteam-alert" onClick={scrollToTaskTable} role="button">
            <span className="md-myteam-alert-icon"><FaTriangleExclamation/></span>
            <span className="md-myteam-alert-text">
              {outsideTeamStats.assigned>0 && <><strong>{outsideTeamStats.assigned}</strong> ticket{outsideTeamStats.assigned===1?"":"s"} outside your tracked team</>}
              {outsideTeamStats.assigned>0 && noOwnerStats.assigned>0 && " · "}
              {noOwnerStats.assigned>0 && <><strong>{noOwnerStats.assigned}</strong> unassigned</>}
            </span>
            <span className="md-myteam-alert-cta">Review Tickets →</span>
          </div>
        )}
      </div>

      </div>{/* /md-tabpanel: team */}

      {/* ══════════════════════════════════════════════════════════
          TAB: Performance Analytics (part A) -- ranking, task status
          donut, team/user task analysis. (Part B -- User Performance
          Report, SLA/aging/priority/category analytics, Team Performance
          Trend -- resumes further down, after the Ticket Management tab's
          content, so this panel is split into two JSX fragments that
          share the same activeTab==="performance" condition.)
      ══════════════════════════════════════════════════════════ */}
      <div className={`md-tabpanel${activeTab==="performance"?" md-tabpanel-active":""}`} style={{display:activeTab==="performance"?undefined:"none"}} role="tabpanel">

      {/* ── Team Performance Ranking ("Top Performers") -- moved here from
           its original position next to Team Workload/Capacity (which now
           lives in the Team Management tab instead). Same performanceRanking/
           userPerfChartData already computed above; no new logic. ── */}
      <div className="md-card md-section-card md-page-section">
        <div className="md-section-head md-team-head">
          <div>
            <h5><FaRankingStar className="md-anacard-icon"/> Top Performers</h5>
            <p>Team performance based on completed tickets in the current view</p>
          </div>
          {performanceRanking.length>0 && (
            <span className="md-topperf-period">{PERIOD_LABELS[gPeriod]||"All Time"}</span>
          )}
        </div>

        {performanceRanking.length===0 ? (
          <div className="md-empty">
            <div className="md-empty-icon"><FaAward/></div>
            <h5>No performance data yet</h5>
            <p>Performance rankings will appear when the team has completed tickets.</p>
          </div>
        ) : (
          <div className="md-topperf-body">
            <ol className="md-topperf-list">
              {performanceRanking.map(m=>(
                <li key={m.id} className={`md-topperf-row${m.rank<=3?` md-topperf-row-${m.rank}`:""}`}>
                  <span className="md-topperf-rankbadge">{m.rank}</span>
                  <div className="md-task-av" aria-hidden="true">{m.name.charAt(0).toUpperCase()}</div>
                  <div className="md-topperf-info">
                    <span className="md-topperf-name" title={m.name}>{m.name}</span>
                    <span className="md-topperf-sub">{m.completed} completed ticket{m.completed===1?"":"s"}</span>
                  </div>
                  <div className="md-topperf-metrics">
                    <div className="md-prog-row">
                      <div className="md-prog-track"><div className="md-prog-fill" style={{width:`${m.completionRate||0}%`}}/></div>
                      <span className="md-prog-pct">{m.completionRate===null?"—":`${m.completionRate}%`}</span>
                    </div>
                    {m.tag==="top" && <span className="md-ranktag md-ranktag-top"><FaAward aria-hidden="true"/> Top Performer</span>}
                    {m.tag==="high-load" && <span className="md-ranktag md-ranktag-warn">High Workload</span>}
                    {m.tag==="attention" && <span className="md-ranktag md-ranktag-danger">Needs Attention</span>}
                  </div>
                </li>
              ))}
            </ol>

            <div className="md-topperf-chart">
              <div className="md-topperf-chart-title">Completed Tickets by Member</div>
              <div className="md-userperf-chartwrap">
                <Bar
                  ref={userPerfChartRef}
                  data={userPerfChartData}
                  options={userPerfChartOptions}
                  plugins={[barValueLabelPlugin]}
                  role="img"
                  aria-label={`Completed tickets by team member: ${performanceRanking.map(m=>`${m.name} ${m.completed}`).join(", ")}`}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── NEW: Ticket Status donut -- reuses statusChartData/statusChartRows/
           statusChartOptions, the exact same derived values already driving
           the User Performance Preview popup's donut (and the segmented-bar
           card just below); nothing new is computed, this just gives the
           Performance Analytics tab its own always-visible donut view of
           the same numbers instead of it being popup-only. ── */}
      <div className="md-card md-section-card md-page-section">
        <div className="md-section-head">
          <h5><FaChartPie className="md-anacard-icon"/> Ticket Status Distribution</h5>
          <p>Same ticket-status counts as the bars below, plotted as a donut.</p>
        </div>
        {statusChartHasData ? (
          <div className="md-userdetail-graphs md-userdetail-graphs-single">
            <div className="md-ud-graph-card">
              <div className="md-ud-donut-wrap">
                <div className="md-ud-donut-center"><strong>{statusChartTotal}</strong><span>Total Tickets</span></div>
                <Doughnut ref={perfStatusChartRef} data={statusChartData} options={statusChartOptions} />
              </div>
              <div className="md-ud-legend">
                {statusChartRows.map(r=>(
                  <div key={r.key} className="md-ud-legend-row">
                    <span className="md-ud-legend-dot" style={{background:r.color}}/>
                    <span className="md-ud-legend-label">{r.label}</span>
                    <span className="md-ud-legend-val">{r.val}</span>
                    <span className="md-ud-legend-pct">{r.pct}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : <div className="md-ud-empty">No tickets in this view yet.</div>}
      </div>

      {/* ── Ticket Status Overview (§8) — THE one primary team-wide status
           visualization. The drill-down panel below has had its own
           internal Ticket Status donut removed (it showed the exact same
           statusBuckets numbers) so this segmented-bar view is now the
           only bar-style rendering of status counts on the page. Status/
           priority/category/SLA colors follow one fixed palette across
           every card on this page: Completed #16A34A, Active/In Progress
           #2563EB, Pending #F59E0B, Warning #F97316, Overdue #DC2626,
           Neutral #64748B. ── */}
      <div className="md-card md-section-card md-page-section">
        <div className="md-section-head">
          <h5><FaChartPie className="md-anacard-icon"/> Ticket Status Overview</h5>
          <p>Workflow status of tickets in the current view (not ownership).</p>
        </div>
        <div className="md-status-bars">
          {[
            {key:"assigned",   label:"Assigned",    val:statusBuckets.assigned,    Icon:FaBriefcase,          cls:"assigned"},
            {key:"pending",    label:"Pending",      val:statusBuckets.pending,     Icon:FaClock,              cls:"pending"},
            {key:"in_progress",label:"In Progress",  val:statusBuckets.in_progress, Icon:FaSpinner,            cls:"progress"},
            {key:"completed",  label:"Completed",    val:statusBuckets.completed,   Icon:FaCircleCheck,        cls:"completed"},
            {key:"overdue",    label:"Overdue",      val:statusBuckets.overdue,     Icon:FaTriangleExclamation,cls:"cancelled"},
          ].map(row=>{
            const pct = dashboardStats.total ? Math.round(row.val/dashboardStats.total*100) : 0;
            const clickable = row.key==="overdue" && row.val>0;
            const RowIcon = row.Icon;
            return (
              <div
                key={row.key}
                className={`md-status-bar-row${clickable?" md-status-bar-row-clickable":""}`}
                role={clickable?"button":undefined}
                tabIndex={clickable?0:undefined}
                onClick={clickable?scrollToTaskTable:undefined}
                title={`${row.label}: ${row.val} ticket${row.val===1?"":"s"} (${pct}% of ${dashboardStats.total})`}
              >
                <span className="md-status-bar-label"><RowIcon/> {row.label}</span>
                <div className="md-status-bar-track">
                  <div className={`md-status-bar-fill ${row.cls}`} style={{width:`${Math.round(row.val/statusBucketMax*100)}%`}}/>
                </div>
                <span className="md-status-bar-val">{row.val} <small>({pct}%)</small></span>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Team Ticket Analysis (user-wise drill-down) ──────────────────
           Team view by default; switches to one member's view the moment
           gMember is set (via a card's View button, or the global Member
           filter itself) — everything below (KPIs/charts/task list) is
           just that same already gMember-scoped data, never a second
           fetch or a parallel filtering path. */}
      <div className="md-card md-section-card md-userdetail-section" ref={userDetailRef}>
        <div className="md-section-head md-team-head">
          <div className="md-ud-titleblock">
            {selectedMemberStats ? (
              <div className="md-ud-user-id">
                <div className="md-myteam-avatar">{selectedMemberStats.name.charAt(0).toUpperCase()}</div>
                <div>
                  <h5>User Ticket Analysis — {selectedMemberStats.name}</h5>
                  <div className="md-ud-user-meta">
                    <span className="md-ud-chip">{selectedMemberStats.role}</span>
                    <span className="md-ud-chip">{PERIOD_LABELS[gPeriod]||"All Time"}</span>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <h5>Team Ticket Analysis</h5>
                <div className="md-ud-scope-chips">
                  {viewingLabel.split(" • ").map((part,i)=><span key={i} className="md-ud-chip">{part}</span>)}
                </div>
              </>
            )}
          </div>
          {selectedMemberStats && (
            <div className="md-ud-head-actions">
              <button className="md-btn md-btn-primary md-btn-sm" onClick={openUserPreview}>
                <FaChartLine/> View Performance Preview
              </button>
              <button className="md-btn md-btn-ghost md-btn-sm" onClick={()=>setGMember("ALL")}>Clear User</button>
            </div>
          )}
        </div>

        {/* compact scope strip (§10) — replaces the old oversized hint block */}
        <div className={`md-ud-strip${selectedMemberStats?" md-ud-strip-active":""}`}>
          {selectedMemberStats ? (
            <>
              <span className="md-ud-strip-text">✓ Showing ticket analysis for <strong>{selectedMemberStats.name}</strong></span>
              <button className="md-ud-strip-cta" onClick={()=>{setGMember(selectedMemberStats.id);scrollToTaskTable();}}>View Tickets →</button>
            </>
          ) : (
            <span className="md-ud-strip-text">ⓘ Showing combined team analysis. Select a member or click View on a team card for individual analysis.</span>
          )}
        </div>

        {/* The Ticket Status donut and the Total/Active/Pending/In Progress/
            Completed/Overdue mini-KPI row that used to sit here have both
            been removed -- they duplicated the standalone Ticket Status
            Overview section above and the page's own KPI Summary at the
            top, respectively. Only the TAT/SLA summary (unique info, not
            shown anywhere else) remains in this panel. */}
        <div className="md-userdetail-graphs md-userdetail-graphs-single">
          <div className="md-ud-graph-card">
            <div className="md-ud-graph-title">{selectedMemberStats ? "TAT / SLA Summary" : "Team SLA Performance"}</div>
            {tatBreakdown.total>0 ? (
              <>
                <div className="md-ud-tat-tiles">
                  <div className="md-ud-tat-tile md-ud-tat-green"><span>Within TAT</span><strong>{tatBreakdown.withinTat}</strong><small>{tatBreakdown.withinTatPct}%</small></div>
                  <div className="md-ud-tat-tile md-ud-tat-amber"><span>At Risk</span><strong>{tatBreakdown.atRisk}</strong><small>{tatBreakdown.atRiskPct}%</small></div>
                  <div className="md-ud-tat-tile md-ud-tat-red"><span>Overdue</span><strong>{tatBreakdown.overdue}</strong><small>{tatBreakdown.overduePct}%</small></div>
                </div>
                <div className="md-ud-stackbar">
                  {tatBreakdown.withinTatPct>0 && <div className="md-ud-stackseg md-ud-seg-green" style={{width:`${tatBreakdown.withinTatPct}%`}} title={`Within TAT: ${tatBreakdown.withinTat} (${tatBreakdown.withinTatPct}%)`}/>}
                  {tatBreakdown.atRiskPct>0 && <div className="md-ud-stackseg md-ud-seg-amber" style={{width:`${tatBreakdown.atRiskPct}%`}} title={`At Risk: ${tatBreakdown.atRisk} (${tatBreakdown.atRiskPct}%)`}/>}
                  {tatBreakdown.overduePct>0 && <div className="md-ud-stackseg md-ud-seg-red" style={{width:`${tatBreakdown.overduePct}%`}} title={`Overdue: ${tatBreakdown.overdue} (${tatBreakdown.overduePct}%)`}/>}
                </div>
                <div className="md-ud-legend">
                  <div className="md-ud-legend-row"><span className="md-ud-legend-dot" style={{background:"#16A34A"}}/><span className="md-ud-legend-label">Within TAT</span><span className="md-ud-legend-val">{tatBreakdown.withinTat}</span></div>
                  <div className="md-ud-legend-row"><span className="md-ud-legend-dot" style={{background:"#D97706"}}/><span className="md-ud-legend-label">At Risk</span><span className="md-ud-legend-val">{tatBreakdown.atRisk}</span></div>
                  <div className="md-ud-legend-row"><span className="md-ud-legend-dot" style={{background:"#DC2626"}}/><span className="md-ud-legend-label">Overdue</span><span className="md-ud-legend-val">{tatBreakdown.overdue}</span></div>
                  {tatBreakdown.noDueDate>0 && <div className="md-ud-legend-row"><span className="md-ud-legend-dot" style={{background:"#9CA3AF"}}/><span className="md-ud-legend-label">No Due Date</span><span className="md-ud-legend-val">{tatBreakdown.noDueDate}</span></div>}
                </div>
              </>
            ) : (
              <div className="md-ud-empty">No tickets with due dates in this view yet.</div>
            )}
          </div>
        </div>

        {selectedMemberStats && (
          <div className="md-userdetail-tasks">
            <div className="md-ud-tasks-head">
              <h6>Tickets for {selectedMemberStats.name}</h6>
              <span className="md-ud-tasks-count">{selectedUserTasks.length} ticket{selectedUserTasks.length===1?"":"s"}</span>
            </div>
            {selectedUserTasks.length===0 ? (
              <div className="md-ud-empty">No tickets for this member in the current filter view.</div>
            ) : (
              <>
                <div className="md-table-wrap">
                  <table className="md-table md-ud-table">
                    <thead>
                      <tr><th>Ticket</th><th style={{width:90}}>Priority</th><th style={{width:130}}>Status</th><th style={{width:110}}>Due / SLA</th><th style={{width:120}}>Progress</th></tr>
                    </thead>
                    <tbody>
                      {(userTasksShowAll?selectedUserTasks:selectedUserTasks.slice(0,10)).map(task=>{
                        const priorityInfo=getPriorityInfo(task.priority);
                        const dueBadge=getDueBadge(task,dashNow);
                        const prog=getTaskProgress(task);
                        return (
                          <tr key={task.id} className={isOverdueTask(task,dashNow)?"md-row-overdue":""}>
                            <td><span className="md-task-name" title={getText(taskDisplayTitle(task))}>{getText(taskDisplayTitle(task))}</span></td>
                            <td>{priorityInfo ? <span className={`md-badge-priority ${priorityInfo.cls}`}>{priorityInfo.label}</span> : <span className="md-no-remark">—</span>}</td>
                            <td><span className={`md-badge ${statusCls(task.status)}`}>{STATUS_LABELS[task.status]||titleCase(task.status)}</span></td>
                            <td><span className={`md-due-badge ${dueBadge.cls}`}>{dueBadge.label}</span></td>
                            <td>
                              <div className="md-prog-row">
                                <div className="md-prog-track"><div className="md-prog-fill" style={{width:`${prog}%`}}/></div>
                                <span className="md-prog-pct">{prog}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {selectedUserTasks.length>10 && (
                  <div className="md-myteam-viewall">
                    <button className="md-btn md-btn-ghost md-btn-sm" onClick={()=>setUserTasksShowAll(v=>!v)}>
                      {userTasksShowAll ? "Show Less" : `View All (${selectedUserTasks.length})`}
                    </button>
                  </div>
                )}
                <div className="md-ud-viewtable-cta">
                  <button className="md-mini-cta" onClick={()=>{setGMember(selectedMemberStats.id);scrollToTaskTable();}}>View all {selectedMemberStats.name}'s tickets →</button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      </div>{/* /md-tabpanel: performance (part A) */}

      {/* ══════════════════════════════════════════════════════════
          TAB: Ticket Management -- daily operational task handling.
      ══════════════════════════════════════════════════════════ */}
      <div className={`md-tabpanel${activeTab==="tasks"?" md-tabpanel-active":""}`} style={{display:activeTab==="tasks"?undefined:"none"}} role="tabpanel">

      {/* ── Main card: Ticket Assignment & Progress ── shared with Manager ->
           Team Complaints (ManagerTaskTable.jsx), with the same work-flow
           tabs. ── */}
      <div className="md-tabs mc-role-tabs md-flow-tabs" role="tablist" aria-label="Team tickets by work flow">
        {WORK_GROUP_TABS.map(([key,label])=>(
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={flowTab===key}
            className={`md-tab${flowTab===key?" active":""}`}
            onClick={()=>changeFlowTab(key)}
          >
            {label} <span className="mc-role-tab-count">({flowCounts[key]})</span>
          </button>
        ))}
      </div>
      <ManagerTaskTable
        tableRef={taskTableRef}
        title="Ticket Assignment & Progress"
        subtitle="All team work by work flow -- Developer, Data Analyst and Technician / IT Support. Each row is assigned within its own flow: Technicians for IT Support complaints, Developers / Data Analysts for their work."
        scopeNote={'User, Priority and Category filters are synced with the dashboard filters above — search and status stay specific to this table.'}
        showCreatedBy
        assignLabel={FLOW_ASSIGN_LABEL[flowTab]||"Assign Owner"}
        rows={flowRows}
        loading={loading}
        now={dashNow}
        search={search} onSearchChange={setSearch}
        status={statusF} onStatusChange={setStatusF} statusOptions={flowStatusRows}
        member={gMember} onMemberChange={setGMember} memberOptions={myTeamMembers}
        quickFilter={quickFilter} onQuickFilterChange={setQuickFilter}
        page={page} onPageChange={setPage}
        rowsPerPage={rowsPerPage} onRowsPerPageChange={setRowsPerPage}
        onReset={resetTaskTable}
        extraFiltersActive={flowTab!=="all"||gPriority!=="ALL"||gCategory!=="ALL"}
        toolbarExtra={<>
          <select className="md-select md-select-priority" aria-label="Priority" value={gPriority} onChange={e=>setGPriority(e.target.value)}>
            <option value="ALL">All Priority</option>
            {priorityOptions.map(p=><option key={p} value={p}>{titleCase(p)}</option>)}
          </select>
          <select className="md-select md-select-category" aria-label="Category" value={gCategory} onChange={e=>setGCategory(e.target.value)}>
            <option value="ALL">All Categories</option>
            {categories.map(c=><option key={getIdValue(c)||getText(c)} value={String(getIdValue(c))}>{getText(c)}</option>)}
          </select>
        </>}
        drafts={drafts} onDraftChange={updateDraft}
        teamMembers={teamMembers}
        onOpen={openTicket}
        onSave={saveAssignment}
        savingId={savingId}
      />

      </div>{/* /md-tabpanel: tasks */}

      {/* ══════════════════════════════════════════════════════════
          TAB: Performance Analytics (part B, continued) -- User
          Performance Report, SLA/aging/priority/category analytics,
          Team Performance Trend.
      ══════════════════════════════════════════════════════════ */}
      <div className={`md-tabpanel${activeTab==="performance"?" md-tabpanel-active":""}`} style={{display:activeTab==="performance"?undefined:"none"}} role="tabpanel">

      {/* ── User Performance Report (formerly "Team Workload Overview") ──
           Same teamRoster scope (search/role, NOT the selected member) as
           My Team above -- selecting one user for drill-down must never
           collapse this table down to one row either. workloadTableRows
           only changes THIS table's row order, never My Team's. Now paginated
           (workloadPageData) instead of rendering every row at once. ── */}
      {teamRoster.length>0 && (
        <div className="md-card md-section-card md-page-section">
          <div className="md-section-head md-team-head">
            <div>
              <h5>User Performance Report</h5>
              <p>Completion, SLA and performance status per team member.</p>
            </div>
            <select className="md-select" value={workloadSort} onChange={e=>setWorkloadSort(e.target.value)}>
              <option value="tasks">Sort: Highest Tickets</option>
              <option value="completion">Sort: Highest Completion</option>
              <option value="overdue">Sort: Highest Overdue</option>
              <option value="sla">Sort: SLA Risk</option>
            </select>
          </div>
          <div className="md-table-wrap">
            <table className="md-table md-workload-table">
              <thead>
                <tr>
                  <th style={{width:40}}>#</th><th>Team Member</th><th>Role</th><th>Total Tickets</th><th>Pending</th><th>In Progress</th>
                  <th>Overdue</th><th>Completed</th><th style={{width:130}}>Completion</th>
                  <th style={{width:70}}>SLA %</th><th>Performance</th><th>Workload</th><th style={{width:110}}></th>
                </tr>
              </thead>
              <tbody>
                {workloadPageData.map((m,rowIdx)=>{
                  const status = ownerStatusInfo(m);
                  const perf = performanceStatusFor(m);
                  const isPreviewing = showUserPreview && previewMemberId===m.id;
                  return (
                  <tr key={m.id||m.name} className={m.overdue>0?"md-row-overdue":""}>
                    <td className="md-ranking-rank">{(workloadPageClamped-1)*workloadRowsPerPage+rowIdx+1}</td>
                    <td>
                      <div className="md-task-cell">
                        <div className="md-task-av">{m.name.charAt(0).toUpperCase()}</div>
                        <span className="md-task-name">{m.name}</span>
                      </div>
                    </td>
                    <td>{m.role}</td>
                    <td><strong>{m.assigned}</strong></td>
                    <td>{m.pending}</td>
                    <td>{m.inProgress}</td>
                    <td className={m.overdue>0?"md-text-danger":""}>{m.overdue}</td>
                    <td>{m.completed}</td>
                    <td>
                      {m.assigned===0 ? <span className="md-no-remark">—</span> : (
                        <div className="md-prog-row">
                          <div className="md-prog-track"><div className="md-prog-fill" style={{width:`${m.completionRate}%`}}/></div>
                          <span className="md-prog-pct">{m.completionRate}%</span>
                        </div>
                      )}
                    </td>
                    <td>{m.slaPct===null ? <span className="md-no-remark">—</span> : `${m.slaPct}%`}</td>
                    <td>{perf ? <span className={`md-perftag md-perftag-${perf.cls}`}>{perf.label}</span> : <span className="md-no-remark">—</span>}</td>
                    <td><span className={`md-wtag ${workloadCls(status.level)}`}>{status.label}</span></td>
                    <td>
                      <button type="button" className="md-viewdetails-btn" onClick={()=>isPreviewing?setShowUserPreview(false):previewUserDetails(m.id)}>
                        <FaEye/> {isPreviewing?"Viewing":"View"}
                      </button>
                    </td>
                  </tr>
                  );
                })}
                {workloadPageClamped===workloadTotalPages && outsideTeamStats.assigned>0 && (
                  <tr className="md-row-outside">
                    <td><span className="md-no-remark">—</span></td>
                    <td>
                      <div className="md-task-cell">
                        <div className="md-task-av md-task-av-warn"><FaTriangleExclamation/></div>
                        <span className="md-task-name">Outside Tracked Team</span>
                      </div>
                    </td>
                    <td>—</td>
                    <td><strong>{outsideTeamStats.assigned}</strong></td>
                    <td>{outsideTeamStats.pending}</td>
                    <td>{outsideTeamStats.inProgress}</td>
                    <td className={outsideTeamStats.overdue>0?"md-text-danger":""}>{outsideTeamStats.overdue}</td>
                    <td>{outsideTeamStats.completed}</td>
                    <td><span className="md-no-remark">—</span></td>
                    <td><span className="md-no-remark">—</span></td>
                    <td><span className="md-no-remark">—</span></td>
                    <td><span className="md-no-remark">Not tracked</span></td>
                    <td></td>
                  </tr>
                )}
                {workloadPageClamped===workloadTotalPages && noOwnerStats.assigned>0 && (
                  <tr className="md-row-unassigned">
                    <td><span className="md-no-remark">—</span></td>
                    <td>
                      <div className="md-task-cell">
                        <div className="md-task-av md-task-av-muted">?</div>
                        <span className="md-task-name">Unassigned</span>
                      </div>
                    </td>
                    <td>—</td>
                    <td><strong>{noOwnerStats.assigned}</strong></td>
                    <td>{noOwnerStats.pending}</td>
                    <td>{noOwnerStats.inProgress}</td>
                    <td className={noOwnerStats.overdue>0?"md-text-danger":""}>{noOwnerStats.overdue}</td>
                    <td>{noOwnerStats.completed}</td>
                    <td><span className="md-no-remark">—</span></td>
                    <td><span className="md-no-remark">—</span></td>
                    <td><span className="md-no-remark">—</span></td>
                    <td><span className="md-no-remark">No owner</span></td>
                    <td></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {workloadTotalPages>1 && (
            <div className="md-footer">
              <span>Showing <strong>{(workloadPageClamped-1)*workloadRowsPerPage+1}</strong>–<strong>{Math.min(workloadPageClamped*workloadRowsPerPage,workloadTableRows.length)}</strong> of <strong>{workloadTableRows.length}</strong></span>
              <div className="md-page-btns">
                <button className="md-page-btn" aria-label="Previous page" disabled={workloadPageClamped===1} onClick={()=>setWorkloadPage(p=>p-1)}>‹</button>
                {[...Array(workloadTotalPages)].map((_,i)=>(
                  <button key={i} className={`md-page-btn${i+1===workloadPageClamped?" active":""}`} aria-label={`Page ${i+1}`} aria-current={i+1===workloadPageClamped?"page":undefined} onClick={()=>setWorkloadPage(i+1)}>{i+1}</button>
                ))}
                <button className="md-page-btn" aria-label="Next page" disabled={workloadPageClamped===workloadTotalPages} onClick={()=>setWorkloadPage(p=>p+1)}>›</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Secondary analytics grid: Performance & SLA Health, Ticket
           Aging, Priority Risk, Top Issue Categories. ── */}
      <div className="md-analytics-grid">
        <div className="md-card md-section-card md-analytics-card">
          <div className="md-section-head">
            <h5><FaChartLine className="md-anacard-icon"/> Team Performance &amp; SLA Health</h5>
            <span className="md-perf-scope">{viewingLabel}</span>
          </div>
          <div className="md-perf-grid">
            <div className="md-perf-tile md-perf-tile-green">
              <div className="md-perf-tile-icon"><FaCircleCheck/></div>
              <div className="md-perf-tile-body">
                <strong>{perfMetrics.completionRate}%</strong>
                <span>Completion Rate</span>
                <small>{dashboardStats.completed} of {dashboardStats.total} ticket{dashboardStats.total===1?"":"s"} completed</small>
              </div>
            </div>
            <div className="md-perf-tile md-perf-tile-blue">
              <div className="md-perf-tile-icon"><FaChartLine/></div>
              <div className="md-perf-tile-body">
                <strong>{perfMetrics.avgProgress}%</strong>
                <span>Average Progress</span>
                <small>Across all tickets in view</small>
              </div>
            </div>
            <div className="md-perf-tile md-perf-tile-green">
              <div className="md-perf-tile-icon"><FaStopwatch/></div>
              <div className="md-perf-tile-body">
                <strong>{perfMetrics.slaCompliance===null?"—":`${perfMetrics.slaCompliance}%`}</strong>
                <span>SLA Compliance</span>
                <small>{dashboardStats.overdue} ticket{dashboardStats.overdue===1?"":"s"} currently at risk/overdue</small>
              </div>
            </div>
            <div className={`md-perf-tile ${perfMetrics.overdueRate>0?"md-perf-tile-red":"md-perf-tile-neutral"}`}>
              <div className="md-perf-tile-icon"><FaTriangleExclamation/></div>
              <div className="md-perf-tile-body">
                <strong className={perfMetrics.overdueRate>0?"md-text-danger":""}>{perfMetrics.overdueRate}%</strong>
                <span>Overdue Rate</span>
                <small>Of tickets in the current view</small>
              </div>
            </div>
          </div>
        </div>

        {agingTotal>0 && (
          <div className="md-card md-section-card md-analytics-card">
            <div className="md-section-head">
              <h5><FaCalendarDays className="md-anacard-icon"/> Ticket Aging Analysis</h5>
              <p>How long still-open tickets have been sitting, by age.</p>
            </div>
            <div className="md-aging-timeline">
              {agingBuckets.map(b=>b.count>0 && (
                <div
                  key={b.key}
                  className={`md-aging-seg md-aging-seg-${b.key}`}
                  style={{width:`${Math.round(b.count/agingTotal*100)}%`}}
                  title={`${b.label}: ${b.count} ticket${b.count===1?"":"s"}`}
                />
              ))}
            </div>
            <div className="md-aging-grid">
              {agingBuckets.map(b=>{
                const attention = b.key==="7d_plus" && b.count>0;
                return (
                  <div
                    key={b.key}
                    className={`md-aging-tile md-aging-tile-${b.key}${attention?" md-aging-tile-attention":""}`}
                    role={attention?"button":undefined}
                    tabIndex={attention?0:undefined}
                    onClick={attention?scrollToTaskTable:undefined}
                    title={`${b.label}: ${b.count} ticket${b.count===1?"":"s"}`}
                  >
                    <span className="md-aging-val">{b.count}</span>
                    <span className="md-aging-lbl">{b.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Priority Risk Analysis (§11) — each bucket now shows its own
             overdue count so the widget answers "what needs attention"
             instead of just a decorative distribution, plus a "View Risk
             Items" CTA on whichever priority currently carries risk. ── */}
        {priorityBuckets.total>0 && (
          <div className="md-card md-section-card md-analytics-card">
            <div className="md-section-head">
              <h5><FaFlag className="md-anacard-icon"/> Priority Risk Analysis</h5>
              <p>Critical / High / Medium / Low across the current view.</p>
            </div>
            <div className="md-status-bars">
              {priorityBuckets.rows.map(row=>{
                const pct = priorityBuckets.total ? Math.round(row.val/priorityBuckets.total*100) : 0;
                return (
                  <div key={row.key} className="md-status-bar-row" title={`${row.label}: ${row.val} ticket${row.val===1?"":"s"}${row.overdue?`, ${row.overdue} overdue`:""} (${pct}%)`}>
                    <span className="md-status-bar-label"><FaFlag style={{color:row.color}}/> {row.label}</span>
                    <div className="md-status-bar-track">
                      <div className="md-status-bar-fill" style={{width:`${pct}%`, background:row.color}}/>
                    </div>
                    <span className="md-status-bar-val">{row.val} <small>({pct}%)</small></span>
                  </div>
                );
              })}
            </div>
            {priorityBuckets.rows.filter(r=>r.overdue>0).map(r=>(
              <div key={`${r.key}-sub`} className="md-priority-risk-sub danger">{r.overdue} {r.label.toLowerCase()}-priority ticket{r.overdue===1?"":"s"} overdue</div>
            ))}
            {priorityBuckets.rows.some(r=>r.overdue>0) && (
              <button type="button" className="md-priority-risk-cta" onClick={scrollToTaskTable}>View Risk Items →</button>
            )}
            {priorityBuckets.unspecified>0 && (
              <button type="button" className="md-priority-unspecified" onClick={scrollToTaskTable}>
                <FaTriangleExclamation/>
                <span className="md-priority-unspecified-text">
                  <strong>{priorityBuckets.unspecified}</strong> Unspecified Priority
                  <small>Data quality issue — review recommended</small>
                </span>
              </button>
            )}
          </div>
        )}

        {/* ── Top Issue Categories -- renamed from "Issue Trend Analysis":
             this is a ranked snapshot of current categories, not a
             time-series, so it no longer claims to be a "trend" (that name
             now belongs solely to the real time-series chart, Team
             Performance Trend, further down the page). ── */}
        {categoryBuckets.rows.length>0 && (
          <div className="md-card md-section-card md-analytics-card">
            <div className="md-section-head">
              <h5><FaLayerGroup className="md-anacard-icon"/> Top Issue Categories</h5>
              <p>Most common categories in the current view.</p>
            </div>
            <div className="md-category-ranklist">
              {(categoryShowAll?categoryBuckets.all:categoryBuckets.rows).map((row,i)=>{
                const pct = dashboardStats.total ? Math.round(row.val/dashboardStats.total*100) : 0;
                return (
                  <div key={row.label} className="md-category-rank-row" title={`${row.label}: ${row.val} ticket${row.val===1?"":"s"} (${pct}%)`}>
                    <span className="md-category-rank-num">{i+1}</span>
                    <div className="md-category-rank-body">
                      <div className="md-category-rank-head">
                        <span className="md-category-rank-label" title={row.label}>{row.label}</span>
                        <span className="md-category-rank-val">{row.val} <small>({pct}%)</small></span>
                      </div>
                      <div className="md-status-bar-track">
                        <div className="md-category-rank-fill" style={{width:`${Math.round(row.val/categoryBuckets.max*100)}%`, opacity:Math.max(0.4, 1-i*0.12)}}/>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {categoryBuckets.all.length>6 && (
              <div className="md-myteam-viewall">
                <button className="md-btn md-btn-ghost md-btn-sm" onClick={()=>setCategoryShowAll(v=>!v)}>
                  {categoryShowAll ? "Show Less" : `View All Categories (${categoryBuckets.all.length})`}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Team Performance Trend ──────────────────────────────────
           Grouped bar chart (chart.js Bar — same library/registration
           already used by this page's Ticket Status donut), replacing the
           earlier CSS-bar rendering for clearer readability. ── */}
      {tasks.length>0 && trendData.hasData && (
        <div className="md-card md-section-card md-page-section">
          <div className="md-section-head md-team-head">
            <div>
              <h5><FaChartLine className="md-anacard-icon"/> Team Performance Trend</h5>
              <p>Created vs. completed vs. pending tickets over time</p>
            </div>
            <div className="md-pill-group md-pill-group-sm">
              {[["daily","Daily"],["weekly","Weekly"],["monthly","Monthly"]].map(([k,l])=>(
                <button key={k} type="button" className={`md-pill-btn${trendGranularity===k?" active":""}`} onClick={()=>setTrendGranularity(k)}>{l}</button>
              ))}
            </div>
          </div>

          <div className="md-trend-context">{trendRangeLabel} · Based on selected dashboard filters</div>

          <div className="md-trend-summary">
            <span><i className="md-dot created"/>Created <strong className="mt-blue">{trendData.createdTotal}</strong></span>
            <span><i className="md-dot completed"/>Completed <strong className="mt-green">{trendData.completedTotal}</strong></span>
            <span><i className="md-dot pending"/>Pending <strong className="mt-amber-text">{trendData.pendingTotal}</strong></span>
          </div>

          <div className={`md-trend-chartwrap${trendData.sparse?" md-trend-chartwrap-compact":""}`}>
            <Bar ref={trendChartRef} data={trendChartData} options={trendChartOptions} />
          </div>
        </div>
      )}

      </div>{/* /md-tabpanel: performance (part B) */}

      {/* ══════════════════════════════════════════════════════════
          TAB: Reports & Insights -- reporting and decision support.
      ══════════════════════════════════════════════════════════ */}
      <div className={`md-tabpanel${activeTab==="reports"?" md-tabpanel-active":""}`} style={{display:activeTab==="reports"?undefined:"none"}} role="tabpanel">

      {/* ── NEW: Report cards -- quick-glance summary tiles over data this
           page already computes (dashboardStats/workloadTableRows/
           perfMetrics/capacityRows), each opening the SAME existing
           Generate Report modal (with its own Download PDF / Download
           Excel actions) rather than a new per-report export path. ── */}
      <div className="md-card md-section-card md-page-section">
        <div className="md-section-head">
          <h5><FaFileExport className="md-anacard-icon"/> Reports</h5>
          <p>Generate a filtered PDF or Excel export of the current dashboard view.</p>
        </div>
        <div className="md-reportcards-grid">
          {[
            {key:"task",   label:"Ticket Report",              Icon:FaListCheck,   val:dashboardStats.total,                                   sub:"tickets in current view"},
            {key:"user",   label:"User Performance Report",   Icon:FaChartSimple, val:workloadTableRows.length,                                sub:"team members ranked"},
            {key:"sla",    label:"SLA Report",                Icon:FaStopwatch,   val:perfMetrics.slaCompliance===null?"—":`${perfMetrics.slaCompliance}%`, sub:"SLA compliance"},
            {key:"workload",label:"Workload Report",          Icon:FaBriefcase,   val:capacityRows.length,                                     sub:"members with active load"},
          ].map(c=>{
            const CardIcon = c.Icon;
            return (
              <div key={c.key} className="md-reportcard">
                <div className="md-reportcard-icon"><CardIcon/></div>
                <div className="md-reportcard-body">
                  <span className="md-reportcard-label">{c.label}</span>
                  <strong className="md-reportcard-val">{c.val}</strong>
                  <span className="md-reportcard-sub">{c.sub}</span>
                </div>
                <button type="button" className="md-btn md-btn-ghost md-btn-sm" onClick={openReportModal} disabled={dashboardStats.total===0}>
                  Generate →
                </button>
              </div>
            );
          })}
        </div>
        <div className="md-reportcards-actions">
          <button className="md-btn md-btn-ghost" onClick={()=>downloadReportExcel()} disabled={loading || !!reportGenerating || dashboardStats.total===0}>
            {reportGenerating==="excel" ? <><Spin/> Exporting…</> : <><FaFileExcel/> Export Excel</>}
          </button>
          <button className="md-btn md-btn-primary" onClick={openReportModal} disabled={loading || dashboardStats.total===0}>
            <FaFilePdf/> Generate PDF
          </button>
        </div>
      </div>

      {/* ── Data Quality Monitoring (§13) — a hygiene/housekeeping signal,
           not an operational priority, so it lives here with the other
           reporting/insight cards rather than in the daily task-management
           view. Always renders (previously vanished entirely when clean)
           with a positive empty state instead of a blank gap. ── */}
      <div className="md-card md-section-card md-page-section">
        <div className="md-section-head"><h5><FaDatabase className="md-anacard-icon"/> Data Quality Monitoring</h5></div>
        {(withoutCategoryCount>0 || missingPriorityCount>0) ? (
          <>
            <div className={`md-dq-score md-dq-score-${dataQualityScore.pct>=90?"good":dataQualityScore.pct>=70?"warn":"bad"}`}>
              <div className="md-dq-score-top">
                <span className="md-dq-score-lbl">Data Quality Health</span>
                <span className="md-dq-score-val">{dataQualityScore.pct}% Complete</span>
              </div>
              <div className="md-status-bar-track">
                <div className="md-dq-score-fill" style={{width:`${dataQualityScore.pct}%`}}/>
              </div>
            </div>
            <div className="md-dataquality-list">
              {withoutCategoryCount>0 && (
                <button type="button" className="md-dataquality-row" onClick={scrollToTaskTable}>
                  <span className="md-dataquality-main">
                    <span className="md-dataquality-val">{withoutCategoryCount}</span>
                    <span className="md-dataquality-lbl">Missing Category</span>
                  </span>
                  <span className="md-dataquality-cta">Review Missing Category →</span>
                </button>
              )}
              {missingPriorityCount>0 && (
                <button type="button" className="md-dataquality-row" onClick={scrollToTaskTable}>
                  <span className="md-dataquality-main">
                    <span className="md-dataquality-val">{missingPriorityCount}</span>
                    <span className="md-dataquality-lbl">Missing Priority</span>
                  </span>
                  <span className="md-dataquality-cta">Review Missing Priority →</span>
                </button>
              )}
            </div>
          </>
        ) : (
          <div className="md-dq-positive">
            <FaCircleCheck aria-hidden="true"/>
            <span>No data quality issues — every ticket in view has a category and a priority set.</span>
          </div>
        )}
      </div>

      {/* ── NEW: Complaints + Staff Approvals + Inventory ────────── */}
      <div className="md-bottom-grid">
        <div className="md-card md-mini-card">
          <div className="md-mini-head">
            <h5>Complaints Overview</h5>
            <span className="md-mini-global">Global · not affected by ticket filters</span>
          </div>
          <div className="md-mini-stats-row">
            <div><span>Open</span><strong className="mt-blue">{complaintsSummary.open}</strong></div>
            <div><span>Pending</span><strong className="mt-amber-text">{complaintsSummary.pending}</strong></div>
            <div><span>Completed</span><strong className="mt-green">{complaintsSummary.completed}</strong></div>
            <div><span>Overdue</span><strong className={complaintsSummary.overdue>0?"md-text-danger":""}>{complaintsSummary.overdue}</strong></div>
          </div>
          <button className="md-mini-cta" onClick={()=>navigate("/manager/complaints")}>View Complaints <FaArrowUpRightFromSquare/></button>
        </div>

        <div className="md-card md-mini-card">
          <div className="md-mini-head">
            <h5>Pending Staff Approvals</h5>
            <span className="md-mini-global">Global · not affected by ticket filters</span>
          </div>
          {approvalLoading ? (
            <div className="md-mini-single-stat"><strong>…</strong><span>Loading</span></div>
          ) : pendingApprovalsCount===0 ? (
            <div className="md-mini-empty">No pending staff approvals</div>
          ) : (
            <>
              <div className="md-mini-single-stat">
                <strong>{pendingApprovalsCount}</strong>
                <span>Awaiting your action</span>
              </div>
              <div className="md-mini-recent-list">
                {approvalQueue.filter(r=>{const s=String(r.status_code||"").toUpperCase();return s.includes("PENDING")||s.includes("WAITING");}).slice(0,5).map(r=>(
                  <div key={r.id} className="md-mini-recent-row">
                    <span className="md-mini-recent-title" title={r.title}>{r.title||`Request #${r.id}`}</span>
                    <span className="md-mini-recent-sub">{r.requester_name||"—"}</span>
                  </div>
                ))}
              </div>
            </>
          )}
          <button className="md-mini-cta" onClick={()=>navigate("/manager/approvals")}>View Approval List <FaArrowUpRightFromSquare/></button>
        </div>

        {(!can || can("products","view")) && (
          <div className="md-card md-mini-card">
            <div className="md-mini-head">
              <h5>Inventory Summary</h5>
              <span className="md-mini-global">Global · not affected by ticket filters</span>
            </div>
            <p className="md-mini-desc">Manage stock, allocations and issued items for your unit.</p>
            <button className="md-mini-cta" onClick={()=>navigate("/manager/products")}>View Products <FaArrowUpRightFromSquare/></button>
          </div>
        )}
      </div>

      {/* Quick Actions now lives in the Overview tab (moved up there so it
           sits with the rest of that tab's high-level, at-a-glance content). */}

      {/* ── Recently Completed Tickets (§16) — completed-only (was previously
           "Recently Updated", mixing in-progress/pending changes too), 5
           records, Ticket/Employee/Completed date shape. ── */}
      {recentCompletedTasks.length>0 && (
        <div className="md-card md-section-card md-page-section">
          <div className="md-section-head md-team-head">
            <div>
              <h5>Recently Completed Tickets</h5>
              <p>The last 5 tickets your team finished.</p>
            </div>
            <button className="md-btn md-btn-ghost md-btn-sm" onClick={()=>{setQuickFilter("completed");scrollToTaskTable();}}>View All →</button>
          </div>
          <div className="md-recent-list">
            {recentCompletedTasks.map(task=>{
              const curName=getText(task.assigned_to_name,"")||getText(task.assigned_to,"")||getText(task.assignedTo,"")||"Unassigned";
              return (
                <div key={task.id} className="md-recent-row">
                  <div className="md-recent-avatar">{curName.charAt(0).toUpperCase()}</div>
                  <div className="md-recent-body">
                    <div className="md-recent-name" title={getText(taskDisplayTitle(task))}>{getText(taskDisplayTitle(task))}</div>
                    <div className="md-recent-text">Completed by {curName}</div>
                  </div>
                  <div className="md-recent-time">{timeAgo(task.updated_at||task.created_at)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      </div>{/* /md-tabpanel: reports */}

      {/* ══ TICKET PANEL (Jira-style right drawer) ══ */}
      {showTicket && (
        <div className="md-ticket-overlay" onClick={e=>e.target===e.currentTarget&&setShowTicket(false)}>
          <div className="md-ticket-panel">
            {/* top bar */}
            <div className="md-ticket-bar">
              <div>
                <div className="md-ticket-id">TASK-{ticketTask?.id||"—"}</div>
                <div className="md-ticket-title">{getText(ticketTask?.title)}</div>
              </div>
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                {ticketTask && <span className={`md-badge ${statusCls(ticketTask.status)}`}>{STATUS_LABELS[ticketTask.status]||titleCase(ticketTask.status)}</span>}
                <button className="md-modal-close" onClick={()=>setShowTicket(false)}><FaXmark/></button>
              </div>
            </div>

            {ticketLoading ? (
              <div className="md-loader"><Spin lg/><p>Loading ticket…</p></div>
            ) : (
              <div className="md-ticket-body">

                {/* ─ LEFT: ticket details ─ */}
                <div className="md-ticket-main">

                  {/* progress */}
                  <div>
                    <div className="md-ticket-section-title">Progress</div>
                    <div className="md-jira-prog">
                      <div className="md-jira-prog-header">
                        <div>
                          <div className="md-jira-prog-pct">{getTaskProgress(ticketTask)}%</div>
                          <div className="md-jira-prog-lbl">{getProgressText(ticketTask)}</div>
                        </div>
                        <span className={`md-badge ${statusCls(ticketTask?.status)}`}>{STATUS_LABELS[ticketTask?.status]||titleCase(ticketTask?.status)}</span>
                      </div>
                      <div className="md-jira-prog-track"><div className="md-jira-prog-fill" style={{width:`${getTaskProgress(ticketTask)}%`}}/></div>
                    </div>
                  </div>

                  {/* description */}
                  <div>
                    <div className="md-ticket-section-title">Description</div>
                    <div className="md-desc-block">{getText(ticketTask?.description,"—")}</div>
                  </div>

                  {/* info grid */}
                  <div>
                    <div className="md-ticket-section-title">Ticket Details</div>
                    <div className="md-info-grid">
                      {[
                        {lbl:"Created By",    val:getText(ticketTask?.created_by?.name,"")||getText(ticketTask?.created_by_name,"")||getText(ticketTask?.created_by,"-")},
                        {lbl:"Requested For", val:getText(ticketTask?.requested_for?.name,"")||getText(ticketTask?.requested_for_name,"")||"—"},
                        {lbl:"Assigned To",   val:getText(ticketTask?.assigned_to?.name,"")||getText(ticketTask?.assigned_to_name,"")||getText(ticketTask?.assigned_to,"Not assigned")},
                        {lbl:"Unit",          val:getText(ticketTask?.unit?.name,"")||getText(ticketTask?.unit_name,"")||getText(ticketTask?.unit_id,"-")},
                        {lbl:"Category",      val:getText(ticketTask?.category?.name,"")||getText(ticketTask?.category_name,"")||getText(ticketTask?.category,"-")},
                        {lbl:"Sub-category",  val:getText(ticketTask?.sub_category?.name,"")||getText(ticketTask?.sub_category_name,"-")},
                        {lbl:"TAT",           val:getTatLabel(ticketTask)},
                        {lbl:"Due Date",      val:ticketTask?.due_at?new Date(ticketTask.due_at).toLocaleString():"-"},
                        {lbl:"Due Status",    val:getDueStatus(ticketTask).label, cls:getDueStatus(ticketTask).cls},
                        {lbl:"Created At",    val:ticketTask?.created_at?new Date(ticketTask.created_at).toLocaleString():"-"},
                        {lbl:"Updated At",    val:ticketTask?.updated_at?new Date(ticketTask.updated_at).toLocaleString():"-"},
                      ].map(c=>(
                        <div key={c.lbl} className="md-info-card">
                          <span>{c.lbl}</span>
                          <strong className={c.cls||""}>{c.val}</strong>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* ─ RIGHT: activity panel ─ */}
                <div className="md-activity-panel">
                  <div className="md-activity-hdr">
                    <div>
                      <h5>Activity</h5>
                      <p>Remarks, chat &amp; status changes</p>
                    </div>
                    <button className="md-btn md-btn-ghost md-btn-sm" onClick={()=>loadTicket(ticketTask,false)}>
                      <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path d="M1 4v6h6M23 20v-6h-6"/><path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4-4.64 4.36A9 9 0 0 1 3.51 15"/>
                      </svg>
                    </button>
                  </div>

                  <div className="md-tabs">
                    {["all","comments","history"].map(t=>(
                      <button key={t} className={`md-tab${activityTab===t?" active":""}`} onClick={()=>setActivityTab(t)}>
                        {t==="all"?"All":t==="comments"?"Remarks / Chat":"Status History"}
                      </button>
                    ))}
                  </div>

                  <div className="md-feed">
                    {activityItems.length===0 ? (
                      <div className="md-feed-empty">
                        <div style={{fontSize:28,marginBottom:6,display:"flex",justifyContent:"center"}}><FaComments/></div>
                        <div>No activity yet</div>
                        <div style={{fontSize:11,color:"var(--md-text-faint, #D6D3D1)"}}>Remarks and status changes appear here</div>
                      </div>
                    ) : activityItems.map(item=>
                      item.kind==="comment" ? (
                        <div key={item.id} className="md-comment">
                          <div className="md-avatar">{getText(item.author,"U").charAt(0).toUpperCase()}</div>
                          <div className="md-comment-body">
                            <div className="md-comment-meta">
                              <strong>{item.author}</strong>
                              <span>{item.time?new Date(item.time).toLocaleString():""}</span>
                            </div>
                            <div className="md-comment-bubble">
                              <span className={`md-badge ${item.type==="general"?"default":item.type}`} style={{marginBottom:5,display:"inline-flex"}}>{item.type}</span>
                              <p style={{margin:0,fontSize:12,color:"var(--md-text-secondary, #57534E)"}}>{item.text}</p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div key={item.id} className="md-history-item">
                          <div className="md-history-dot"/>
                          <div className="md-history-body">
                            <p><strong>{item.author}</strong> changed status from <b>{titleCase(item.from||"Start")}</b> → <b>{titleCase(item.to)}</b></p>
                            <small>{item.time?new Date(item.time).toLocaleString():""}</small>
                            {item.note&&<p className="md-history-note">Note: {getText(item.note)}</p>}
                          </div>
                        </div>
                      )
                    )}
                    <div ref={feedEndRef}/>
                  </div>

                  {/* comment input */}
                  <div className="md-comment-input-area">
                    <textarea
                      className="md-comment-textarea"
                      rows={2}
                      placeholder="Add manager comment… (Enter to send)"
                      value={comment}
                      onChange={e=>setComment(e.target.value)}
                      onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();addComment();}}}
                    />
                    <div className="md-comment-actions">
                      <span className="md-comment-hint">Visible to technician in chat</span>
                      <button className="md-btn md-btn-primary md-btn-sm" onClick={addComment} disabled={commentSaving||!comment.trim()}>
                        {commentSaving?<><Spin white/> Posting…</>:"Add Comment"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══ CREATE MODAL ══ */}
      {showCreate && (
        <div className="md-overlay" onClick={e=>e.target===e.currentTarget&&setShowCreate(false)}>
          <div className="md-modal">
            <div className="md-modal-hdr">
              <h2 className="md-modal-title">Create New Ticket</h2>
              <button className="md-modal-close" onClick={()=>setShowCreate(false)}><FaXmark/></button>
            </div>
            <div className="md-modal-body">
              <div className="md-form-group">
                <label className="md-label">Title <span style={{color:"var(--md-danger, #EF4444)"}}>*</span></label>
                <input className="md-input" placeholder="Ticket title" value={createForm.title} onChange={e=>setCreateForm(p=>({...p,title:e.target.value}))} />
              </div>
              <div className="md-form-group">
                <label className="md-label">Description</label>
                <textarea className="md-textarea" rows={3} placeholder="Describe the ticket…" value={createForm.description} onChange={e=>setCreateForm(p=>({...p,description:e.target.value}))} />
              </div>
              <div className="md-form-row">
                <div className="md-form-group">
                  <label className="md-label">Unit <span style={{color:"var(--md-danger, #EF4444)"}}>*</span></label>
                  <input className="md-form-select" value={ownUnitName} readOnly aria-label="Unit" />

                </div>
                <div className="md-form-group">
                  <label className="md-label">Category <span style={{color:"var(--md-danger, #EF4444)"}}>*</span></label>
                  <select className="md-form-select" value={createForm.category_id} disabled={!createForm.unit_id} onChange={e=>setCreateForm(p=>({...p,category_id:e.target.value}))}>
                    <option value="">Select Category</option>
                    {createCats.map(c=><option key={getIdValue(c)||getText(c)} value={getIdValue(c)}>{getText(c)}</option>)}
                  </select>
                </div>
              </div>
              <div className="md-form-row">
                <div className="md-form-group">
                  <label className="md-label">Subcategory <span style={{color:"var(--md-danger, #EF4444)"}}>*</span></label>
                  <select className="md-form-select" value={createForm.sub_category_id} disabled={!createForm.category_id} onChange={e=>setCreateForm(p=>({...p,sub_category_id:e.target.value}))}>
                    <option value="">Select Subcategory</option>
                    {createSubs.map(s=><option key={getIdValue(s)||getText(s)} value={getIdValue(s)}>{getText(s)}</option>)}
                  </select>
                </div>
                <div className="md-form-group">
                  <label className="md-label">Assign Technician</label>
                  <select className="md-form-select" value={createForm.assigned_to} onChange={e=>setCreateForm(p=>({...p,assigned_to:e.target.value}))}>
                    <option value="">Auto assign / later</option>
                    {technicians.map(t=><option key={getIdValue(t)||getText(t)} value={getIdValue(t)}>{getText(t)}</option>)}
                  </select>
                </div>
              </div>
              {createForm.sub_category_id && (
                <div className="md-form-group">
                  <label className="md-label">Estimated TAT</label>
                  {createSubHasTat ? (
                    <input className="md-input" readOnly value={`${selectedCreateSub.tat_value} ${selectedCreateSub.tat_unit}`} />
                  ) : (
                    <div style={{fontSize:12,color:"var(--md-text-muted, #A8A29E)"}}>
                      No Turnaround Time is configured for this subcategory yet — it will be created without a due date.
                    </div>
                  )}
                </div>
              )}
              <div className="md-form-group">
                <label className="md-label">Ticket Date <span style={{color:"var(--md-danger, #EF4444)"}}>*</span></label>
                <input type="date" className="md-input" max={todayDateString()} value={createForm.task_date} onChange={e=>setCreateForm(p=>({...p,task_date:e.target.value}))} />
              </div>
              <div style={{fontSize:11,color:"var(--md-text-muted, #A8A29E)"}}>Fields marked * are required.</div>
            </div>
            <div className="md-modal-ftr">
              <button className="md-btn md-btn-ghost" onClick={()=>setShowCreate(false)} disabled={creating}>Cancel</button>
              <button className="md-btn md-btn-primary" onClick={createTask}
                disabled={creating||!createForm.title||!createForm.unit_id||!createForm.category_id||!createForm.sub_category_id||!createForm.task_date}>
                {creating?<><Spin white/> Creating…</>:"Create Ticket"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── NEW: Generate Report preview modal ────────────────────── */}
      {showReportModal && (
        <div className="md-overlay" onClick={e=>e.target===e.currentTarget&&setShowReportModal(false)}>
          <div className="md-modal md-modal-report">
            <div className="md-modal-hdr">
              <h2 className="md-modal-title">Manager Ticket Performance Report</h2>
              <button className="md-modal-close" onClick={()=>setShowReportModal(false)}><FaXmark/></button>
            </div>
            <div className="md-modal-body">
              <div className="md-report-block-lbl">Report Filters</div>
              <div className="md-filterbar-row md-userpreview-filterbar">
                <div className="md-pill-group md-pill-group-sm">
                  {[["all","All Time"],["today","Today"],["week","This Week"],["month","This Month"],["custom","Custom"]].map(([k,l])=>(
                    <button key={k} type="button" className={`md-pill-btn${reportPeriod===k?" active":""}`} onClick={()=>setReportPeriod(k)}>{l}</button>
                  ))}
                </div>
                {reportPeriod==="custom" && (
                  <div className="md-filter-daterow">
                    <input type="date" className="md-select" value={reportCustomFrom} onChange={e=>setReportCustomFrom(e.target.value)} />
                    <span className="md-filter-to">to</span>
                    <input type="date" className="md-select" value={reportCustomTo} onChange={e=>setReportCustomTo(e.target.value)} />
                  </div>
                )}
                <select className="md-select" value={reportMember} onChange={e=>setReportMember(e.target.value)}>
                  <option value="ALL">All Members</option>
                  {myTeamMembers.map(m=><option key={getIdValue(m)||getText(m)} value={getIdValue(m)}>{getText(m)}</option>)}
                </select>
              </div>

              <div className="md-report-block-lbl">Selected Filters</div>
              <div className="md-report-filterchips">
                {reportModalFilterChips.map(c=><span key={c.key} className="md-report-chip">{c.label}</span>)}
                {hasActiveReportFilters && (
                  <button type="button" className="md-report-clear" onClick={resetReportFilters}>Clear Filters</button>
                )}
              </div>

              <div className="md-report-total">
                <strong>{reportFilteredTasks.length}</strong> Ticket{reportFilteredTasks.length===1?"":"s"} match the current filters
              </div>

              <div className="md-report-block-lbl">KPI Preview</div>
              <div className="md-report-kpis">
                <div className="md-report-kpi"><span>Total</span><strong>{reportStats.assigned}</strong></div>
                <div className="md-report-kpi mt-green"><span>Completed</span><strong>{reportStats.completed}</strong></div>
                <div className="md-report-kpi mt-amber-text"><span>Pending</span><strong>{reportStats.pending}</strong></div>
                <div className="md-report-kpi mt-blue"><span>In Progress</span><strong>{reportStats.inProgress}</strong></div>
                <div className="md-report-kpi" style={{color:reportStats.overdue>0?"#DC2626":undefined}}><span>Overdue</span><strong>{reportStats.overdue}</strong></div>
                <div className="md-report-kpi mt-green"><span>Completion</span><strong>{reportStats.completionRate??0}%</strong></div>
                <div className="md-report-kpi mt-green"><span>SLA</span><strong>{reportStats.slaPct===null?"—":`${reportStats.slaPct}%`}</strong></div>
              </div>

              <div className="md-report-block-lbl">This Report Includes</div>
              <ul className="md-report-checklist">
                <li>KPI Summary</li>
                <li>Team Performance ({reportTeamRows.length} member{reportTeamRows.length===1?"":"s"})</li>
                <li>Ticket Details ({reportFilteredTasks.length} ticket{reportFilteredTasks.length===1?"":"s"})</li>
                <li>Analytics Summary (status, priority, category, SLA)</li>
                <li>Graph Snapshots (PDF only)</li>
              </ul>
            </div>
            <div className="md-modal-ftr">
              <button className="md-btn md-btn-ghost" onClick={()=>setShowReportModal(false)} disabled={!!reportGenerating}>Cancel</button>
              <button className="md-btn md-btn-ghost" onClick={()=>downloadReportExcel("report")} disabled={!!reportGenerating || reportFilteredTasks.length===0}>
                {reportGenerating==="excel" ? <><Spin/> Preparing…</> : <><FaFileExcel/> Download Excel</>}
              </button>
              <button className="md-btn md-btn-primary" onClick={()=>downloadReportPdf("report")} disabled={!!reportGenerating || reportFilteredTasks.length===0}>
                {reportGenerating==="pdf" ? <><Spin white/> Preparing Report…</> : <><FaFilePdf/> Download PDF</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── NEW: User Performance Preview popup (§9) ──────────────────
           Reuses selectedMemberStats/selectedUserTasks/statusChartData/
           statusChartRows/tatBreakdown/trendData -- the SAME already-
           computed values the inline "Team Ticket Analysis" section below
           uses (both derive from globalFilteredTasks, which is already
           scoped to gMember). No new fetch, no new derived dataset beyond
           the popup-only slaBarChartData/productivityTrendChartData chart
           configs defined above (themselves built from tatBreakdown/
           trendData). Download button calls the popup's OWN
           downloadUserPerformancePdf -- see that function's comment for why
           it's kept separate from the shared downloadReportPdf. */}
      {showUserPreview && popupMemberStats && createPortal(
        <div className="md-overlay md-overlay-userpreview" onClick={e=>e.target===e.currentTarget&&setShowUserPreview(false)}>
          <div className="md-modal md-modal-userpreview">
            <div className="md-modal-hdr">
              <div className="md-userpreview-id">
                <div className="md-myteam-avatar">{popupMemberStats.name.charAt(0).toUpperCase()}</div>
                <div>
                  <div className="md-userpreview-eyebrow">User Performance Report</div>
                  <h2 className="md-modal-title">{popupMemberStats.name}</h2>
                  <div className="md-ud-user-meta">
                    <span className="md-ud-chip">{popupMemberStats.role}</span>
                    <span className="md-ud-chip">Reporting Period: {previewPeriodLabel}</span>
                  </div>
                </div>
              </div>
              <button className="md-modal-close" onClick={()=>setShowUserPreview(false)}><FaXmark/></button>
            </div>
            <div className="md-modal-body">
              <div className="md-filterbar-row md-userpreview-filterbar">
                <div className="md-pill-group md-pill-group-sm">
                  {[["all","All Time"],["today","Today"],["week","This Week"],["month","This Month"],["custom","Custom"]].map(([k,l])=>(
                    <button key={k} type="button" className={`md-pill-btn${previewPeriod===k?" active":""}`} onClick={()=>setPreviewPeriod(k)}>{l}</button>
                  ))}
                </div>
                {previewPeriod==="custom" && (
                  <div className="md-filter-daterow">
                    <input type="date" className="md-select" value={previewCustomFrom} onChange={e=>setPreviewCustomFrom(e.target.value)} />
                    <span className="md-filter-to">to</span>
                    <input type="date" className="md-select" value={previewCustomTo} onChange={e=>setPreviewCustomTo(e.target.value)} />
                  </div>
                )}
              </div>

              <div className="md-userdetail-kpis md-userpreview-kpis">
                <div className="md-ud-kpi md-ud-kpi-total"><FaListCheck className="md-ud-kpi-icon"/><span>Total Tickets</span><strong>{previewStats.assigned}</strong></div>
                <div className="md-ud-kpi md-ud-kpi-completed"><FaCircleCheck className="md-ud-kpi-icon"/><span>Completed</span><strong>{previewStats.completed}</strong></div>
                <div className="md-ud-kpi md-ud-kpi-pending"><FaClock className="md-ud-kpi-icon"/><span>Pending</span><strong>{previewStats.pending}</strong></div>
                <div className="md-ud-kpi md-ud-kpi-inprogress"><FaSpinner className="md-ud-kpi-icon"/><span>In Progress</span><strong>{previewStats.inProgress}</strong></div>
                <div className="md-ud-kpi md-ud-kpi-overdue"><FaTriangleExclamation className="md-ud-kpi-icon"/><span>Overdue</span><strong>{previewStats.overdue}</strong></div>
                <div className="md-ud-kpi md-ud-kpi-completionrate"><FaCheckDouble className="md-ud-kpi-icon"/><span>Completion Rate</span><strong>{previewStats.completionRate===null?"—":`${previewStats.completionRate}%`}</strong></div>
                <div className="md-ud-kpi md-ud-kpi-sla"><FaStopwatch className="md-ud-kpi-icon"/><span>SLA Compliance</span><strong>{previewStats.slaPct===null?"—":`${previewStats.slaPct}%`}</strong></div>
              </div>

              <div className="md-userdetail-graphs md-userpreview-graphs">
                <div className="md-ud-graph-card">
                  <div className="md-ud-graph-title"><FaChartPie className="md-anacard-icon"/> Ticket Status</div>
                  {previewStatusHasData ? (
                    <>
                      <div className="md-ud-donut-wrap">
                        <div className="md-ud-donut-center"><strong>{previewStatusTotal}</strong><span>Total Tickets</span></div>
                        <Doughnut ref={userPreviewStatusChartRef} data={previewStatusChartData} options={previewStatusChartOptions} />
                      </div>
                      <div className="md-ud-legend">
                        {previewStatusRows.map(r=>(
                          <div key={r.key} className="md-ud-legend-row">
                            <span className="md-ud-legend-dot" style={{background:r.color}}/>
                            <span className="md-ud-legend-label">{r.label}</span>
                            <span className="md-ud-legend-val">{r.val}</span>
                            <span className="md-ud-legend-pct">{r.pct}%</span>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : <div className="md-ud-empty">No tickets in this view yet.</div>}
                </div>

                <div className="md-ud-graph-card">
                  <div className="md-ud-graph-title"><FaStopwatch className="md-anacard-icon"/> SLA Performance</div>
                  {previewTatBreakdown.total>0 ? (
                    <>
                      <div className="md-ud-tat-tiles">
                        <div className="md-ud-tat-tile md-ud-tat-green"><span>Within SLA</span><strong>{previewTatBreakdown.withinTat}</strong><small>{previewTatBreakdown.withinTatPct}%</small></div>
                        <div className="md-ud-tat-tile md-ud-tat-amber"><span>Near Risk</span><strong>{previewTatBreakdown.atRisk}</strong><small>{previewTatBreakdown.atRiskPct}%</small></div>
                        <div className="md-ud-tat-tile md-ud-tat-red"><span>Overdue</span><strong>{previewTatBreakdown.overdue}</strong><small>{previewTatBreakdown.overduePct}%</small></div>
                      </div>
                      <div className="md-userpreview-chartwrap">
                        <Bar ref={userPreviewSlaChartRef} data={previewSlaBarChartData} options={slaBarChartOptions} />
                      </div>
                    </>
                  ) : <div className="md-ud-empty">No tickets with due dates in this view yet.</div>}
                </div>

                <div className="md-ud-graph-card">
                  <div className="md-ud-graph-title"><FaChartLine className="md-anacard-icon"/> Productivity Trend</div>
                  {previewTrendData.hasData ? (
                    <div className="md-userpreview-chartwrap md-userpreview-chartwrap-tall">
                      <Line ref={userPreviewTrendChartRef} data={previewTrendChartData} options={productivityTrendChartOptions} />
                    </div>
                  ) : <div className="md-ud-empty">Not enough completion activity to chart a trend yet.</div>}
                </div>
              </div>

              <div className="md-userdetail-tasks md-userpreview-tasklist">
                <div className="md-ud-tasks-head">
                  <h6>Ticket List</h6>
                  <span className="md-ud-tasks-count">{previewFilteredTasks.length} ticket{previewFilteredTasks.length===1?"":"s"}</span>
                </div>
                {previewFilteredTasks.length===0 ? (
                  <div className="md-ud-empty">No tickets for this member in the current filter view.</div>
                ) : (
                  <div className="md-table-wrap">
                    <table className="md-table md-ud-table">
                      <thead>
                        <tr><th>Ticket Name</th><th style={{width:90}}>Priority</th><th style={{width:120}}>Status</th><th style={{width:100}}>Due / SLA</th><th style={{width:100}}>Completion %</th><th style={{width:130}}>Category</th></tr>
                      </thead>
                      <tbody>
                        {previewFilteredTasks.slice(0,8).map(task=>{
                          const priorityInfo=getPriorityInfo(task.priority);
                          const dueBadge=getDueBadge(task,dashNow);
                          return (
                            <tr key={task.id} className={isOverdueTask(task,dashNow)?"md-row-overdue":""}>
                              <td><span className="md-task-name" title={getText(taskDisplayTitle(task))}>{getText(taskDisplayTitle(task))}</span></td>
                              <td>{priorityInfo ? <span className={`md-badge-priority ${priorityInfo.cls}`}>{priorityInfo.label}</span> : <span className="md-no-remark">—</span>}</td>
                              <td><span className={`md-badge ${statusCls(task.status)}`}>{STATUS_LABELS[task.status]||titleCase(task.status)}</span></td>
                              <td><span className={`md-due-badge ${dueBadge.cls}`}>{dueBadge.label}</span></td>
                              <td>{getTaskProgress(task)}%</td>
                              <td>{getText(task.category_name,"")||"Uncategorized"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                {previewFilteredTasks.length>8 && (
                  <div className="md-ud-viewtable-cta">
                    <button className="md-mini-cta" onClick={()=>{setShowUserPreview(false);setGMember(popupMemberStats.id);scrollToTaskTable();}}>View all {previewFilteredTasks.length} tickets →</button>
                  </div>
                )}
              </div>
            </div>
            <div className="md-modal-ftr">
              <button className="md-btn md-btn-ghost" onClick={()=>setShowUserPreview(false)}>Close</button>
              <button className="md-btn md-btn-ghost" onClick={downloadUserPerformanceExcel} disabled={!!reportGenerating || previewStats.assigned===0}>
                {reportGenerating==="excel" ? <><Spin/> Preparing…</> : <><FaFileExcel/> Download Excel</>}
              </button>
              <button className="md-btn md-btn-primary" onClick={downloadUserPerformancePdf} disabled={!!reportGenerating || previewStats.assigned===0}>
                {reportGenerating==="pdf" ? <><Spin white/> Preparing Report…</> : <><FaDownload/> Download Report</>}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── Toast ──────────────────────────────── */}
      {toast.show && (
        <div className={`md-toast ${toast.variant}`}>
          {toast.variant==="success"?<FaCircleCheck/>:<FaCircleXmark/>} {toast.msg}
        </div>
      )}
    </div>
  );
}
